import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { cms, productName, productDescription } from '../i18n/localize';
import {
  CheckCircle, Loader2, ShoppingCart, Plus, Minus,
  MessageCircle, ChevronRight, ChevronLeft, ChefHat, UtensilsCrossed,
  Phone, MapPin, Users, RefreshCw, X, Search, AlertCircle
} from 'lucide-react';
import SiteHeader from '../components/SiteHeader';
import SiteFooter from '../components/SiteFooter';
import { apiFetch } from '../lib/api';
import {
  trackEvent,
  trackProductView,
  trackAddToCart,
  trackBeginCheckout,
  trackOrderCreated,
  trackWhatsAppClick,
} from '../lib/analytics';
import { DEFAULT_HOME_SECTIONS, type HomePage, type HomePageSection } from '../lib/homeDefaults';
import type { HeroSection, AboutSection } from './BrandStory';



// 「其他屋苑」需求登記（demand capture）：下拉選項值與香港 18 區常量
const OTHER_ESTATE = '__other__';
// 屋苑需求登記地區：香港島/九龍/新界 三大分區 + 細緻地區（同 worker HK_DISTRICTS 同步）
const HK_DISTRICT_GROUPS: { region: string; areas: string[] }[] = [
  {
    region: '香港島',
    areas: ['上環/中環/金鐘', '灣仔/銅鑼灣/跑馬地', '北角/鰂魚涌', '太古/西灣河/筲箕灣', '柴灣/小西灣', '香港仔/鴨脷洲', '薄扶林/數碼港', '山頂/半山區'],
  },
  {
    region: '九龍',
    areas: ['尖沙咀/佐敦', '油麻地', '旺角/大角咀', '紅磡/何文田', '九龍城/土瓜灣', '啟德/九龍灣', '黃大仙/新蒲崗', '彩虹/慈雲山', '觀塘/藍田', '深水埗/長沙灣', '石硤尾/九龍塘'],
  },
  {
    region: '新界',
    areas: ['荃灣', '葵涌/葵芳', '青衣', '屯門', '元朗', '天水圍', '大埔', '沙田/大圍', '馬鞍山', '上水/粉嶺', '將軍澳', '西貢', '東涌', '離島'],
  },
];

type DeliveryRegion = 'NT' | 'KL' | 'HK';

function linkifyDescription(text: string): React.ReactNode[] {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);
  return parts.map((part, i) => {
    if (part.startsWith('http://') || part.startsWith('https://')) {
      return (
        <a
          key={i}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="underline text-[#C2410C] hover:text-[#9a3412] break-all"
          onClick={(e) => e.stopPropagation()}
        >
          {part}
        </a>
      );
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

function renderDescription(text?: string) {
  if (!text) return null;
  return text.split('\n').map((line, i) => {
    // Convert markdown-like *bold* to <strong> for display
    const html = line.replace(/\*(.*?)\*/g, '<strong>$1</strong>');
    return (
      <p key={i} className={i > 0 ? 'mt-3' : ''} dangerouslySetInnerHTML={{ __html: html }} />
    );
  });
}

function formatWeekday(dateStr: string): string {
  if (!dateStr) return '';
  const date = new Date(dateStr + 'T00:00:00');
  if (Number.isNaN(date.getTime())) return '';
  // 星期名稱跟隨介面語言（zh-HK → 星期X；en → Weekday）
  const locale = (typeof i18n !== 'undefined' && i18n.language === 'en') ? 'en-GB' : 'zh-HK';
  return date.toLocaleDateString(locale, { weekday: 'long' });
}

// 「好餸·鄰里」運作流程圖彈窗：首次訪問自動顯示，客人可關閉或今日不再顯示
function ShowAndTellModal({
  isOpen,
  onClose,
  onDontShowAgain,
}: {
  isOpen: boolean;
  onClose: () => void;
  onDontShowAgain: () => void;
}) {
  const [dontShow, setDontShow] = useState(false);
  const { t } = useTranslation();
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-3">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-[#F9F6F0] dark:bg-stone-900 rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-orange-100 dark:border-stone-700">
          <h3 className="text-lg font-bold text-[#C2410C]">{t('home:showAndTell.title')}</h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-stone-200 dark:hover:bg-stone-700 text-stone-500 transition"
          >
            <X size={20} />
          </button>
        </div>
        <div className="overflow-y-auto p-1">
          <img
            src="/showtotell.jpeg?v=2"
            alt={t('home:showAndTell.imageAlt')}
            className="w-full h-auto rounded-xl"
            loading="lazy"
          />
        </div>
        <div className="px-4 py-3 border-t border-orange-100 dark:border-stone-700 space-y-3">
          <label className="flex items-center gap-2 text-sm text-stone-700 dark:text-stone-300 cursor-pointer">
            <input
              type="checkbox"
              checked={dontShow}
              onChange={(e) => {
                setDontShow(e.target.checked);
                if (e.target.checked) onDontShowAgain();
              }}
              className="w-4 h-4 rounded border-stone-300 text-[#C2410C] focus:ring-[#C2410C]"
            />
            {t('home:showAndTell.dontShowToday')}
          </label>
          <button
            type="button"
            onClick={onClose}
            className="w-full py-2.5 bg-[#C2410C] hover:bg-[#9a3412] text-white font-bold rounded-lg transition"
          >
            {t('home:showAndTell.startShopping')}
          </button>
        </div>
      </div>
    </div>
  );
}

function hktDateStr(now = new Date()): string {
  return now.toLocaleDateString('en-CA', { timeZone: 'Asia/Hong_Kong' });
}

function hktDateTimeParts(now: Date): { dateStr: string; hour: number; minute: number } {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Hong_Kong',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const parts = fmt.formatToParts(now);
  const get = (type: string) => parts.find((p) => p.type === type)?.value || '0';
  return {
    dateStr: `${get('year')}-${get('month')}-${get('day')}`,
    hour: Number(get('hour')),
    minute: Number(get('minute')),
  };
}

function addDaysHkt(dateStr: string, days: number): string {
  const d = new Date(`${dateStr}T00:00:00+08:00`);
  d.setDate(d.getDate() + days);
  return d.toLocaleDateString('en-CA', { timeZone: 'Asia/Hong_Kong' });
}

function dayOfWeekHkt(dateStr: string): number {
  return new Date(`${dateStr}T00:00:00+08:00`).getDay();
}

function getNextMerchantDeliveryDate(
  now: Date,
  deliveryDays: number[],
  cutoffTime: string,
  cutoffDaysBefore: number
): string {
  if (!deliveryDays.length) {
    const { dateStr: today, hour, minute } = hktDateTimeParts(now);
    const [ch, cm] = cutoffTime.split(':').map(Number);
    const beforeCutoff = hour * 60 + minute < (ch || 0) * 60 + (cm || 0);
    return addDaysHkt(today, beforeCutoff ? 1 : 2);
  }
  const uniqueDays = Array.from(new Set(deliveryDays)).sort((a, b) => a - b);
  const { dateStr: today, hour, minute } = hktDateTimeParts(now);
  const [ch, cm] = cutoffTime.split(':').map(Number);
  const beforeCutoff = hour * 60 + minute < (ch || 0) * 60 + (cm || 0);
  // 截單前 N 天：在截單時間前下單，最早可送日 = 今天 + N；截單後則再順延一天
  const leadDays = beforeCutoff ? (cutoffDaysBefore || 0) : ((cutoffDaysBefore || 0) + 1);
  let anchor = addDaysHkt(today, leadDays);
  const anchorDow = dayOfWeekHkt(anchor);
  for (let i = 0; i <= 14; i++) {
    const candidate = addDaysHkt(anchor, i);
    const dow = (anchorDow + i) % 7;
    if (uniqueDays.includes(dow)) return candidate;
  }
  return addDaysHkt(today, beforeCutoff ? 1 : 2);
}

function isMerchantDeliveryDay(dateStr: string, deliveryDays: number[]): boolean {
  if (!deliveryDays.length) return true;
  return deliveryDays.includes(dayOfWeekHkt(dateStr));
}

function getNextAllowedDeliveryDate(dateStr: string, deliveryDays: number[]): string {
  if (!deliveryDays.length) return dateStr;
  const uniqueDays = Array.from(new Set(deliveryDays)).sort((a, b) => a - b);
  if (isMerchantDeliveryDay(dateStr, uniqueDays)) return dateStr;
  const startDow = dayOfWeekHkt(dateStr);
  for (let i = 1; i <= 14; i++) {
    const candidate = addDaysHkt(dateStr, i);
    const dow = (startDow + i) % 7;
    if (uniqueDays.includes(dow)) return candidate;
  }
  return dateStr;
}

function formatDeliveryDays(days: number[]): string {
  const labels = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
  // 分隔符跟隨介面語言（zh 頓號；en 逗號）
  const sep = (typeof i18n !== 'undefined' && i18n.language === 'en') ? ', ' : '、';
  return days.map((d) => labels[d]).join(sep);
}

// 獨立組件：屋苑需求登記（避免在 OrderLanding 內以 render function 形式使用 hooks 導致 step 切換時 hook 數量不一致）
type RequesterType = 'consumer' | 'sen_youth' | 'sen_parent';
const REQUESTER_TYPE_OPTIONS: { value: RequesterType; labelKey: string; descKey: string }[] = [
  { value: 'consumer', labelKey: 'home:estateRequest.types.consumer.label', descKey: 'home:estateRequest.types.consumer.desc' },
  { value: 'sen_youth', labelKey: 'home:estateRequest.types.sen_youth.label', descKey: 'home:estateRequest.types.sen_youth.desc' },
  { value: 'sen_parent', labelKey: 'home:estateRequest.types.sen_parent.label', descKey: 'home:estateRequest.types.sen_parent.desc' },
];

const EstateRequestSection: React.FC<{ gsCode?: string }> = ({ gsCode }) => {
  const { t } = useTranslation();
  const [localLeadEstate, setLocalLeadEstate] = useState('');
  const [localLeadDistrict, setLocalLeadDistrict] = useState('');
  const [localLeadPhone, setLocalLeadPhone] = useState('');
  const [localLeadType, setLocalLeadType] = useState<RequesterType | ''>('');
  const [localLeadStatus, setLocalLeadStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [localLeadError, setLocalLeadError] = useState('');

  const handleLocalLeadSubmit = async () => {
    setLocalLeadError('');
    if (!localLeadType) { setLocalLeadError(t('home:estateRequest.errors.typeRequired')); return; }
    if (!localLeadEstate.trim()) { setLocalLeadError(t('home:estateRequest.errors.estateRequired')); return; }
    if (!localLeadDistrict) { setLocalLeadError(t('home:estateRequest.errors.districtRequired')); return; }
    const digits = localLeadPhone.replace(/\D/g, '');
    const normalizedPhone = digits.length === 8 ? `852${digits}` : digits;
    if (!/^852\d{8}$/.test(normalizedPhone)) { setLocalLeadError(t('home:estateRequest.errors.invalidPhone')); return; }

    setLocalLeadStatus('submitting');
    try {
      const res = await apiFetch('/api/public/estate-requests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          estateName: localLeadEstate.trim(),
          district: localLeadDistrict,
          phone: normalizedPhone,
          name: '',
          referralCode: gsCode || '',
          requesterType: localLeadType,
        }),
      });
      if (res.ok) {
        setLocalLeadStatus('success');
        setLocalLeadEstate('');
        setLocalLeadDistrict('');
        setLocalLeadPhone('');
        setLocalLeadType('');
      } else {
        const data = await res.json().catch(() => ({}));
        setLocalLeadError(data.error || t('home:estateRequest.errors.submitFailed'));
        setLocalLeadStatus('error');
      }
    } catch {
      setLocalLeadError(t('home:estateRequest.errors.network'));
      setLocalLeadStatus('error');
    }
  };

  return (
    <section id="estate-request" className="py-12 md:py-16 px-4 bg-amber-50 dark:bg-stone-900 scroll-mt-24">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-6">
          <h2 className="text-2xl md:text-3xl font-bold text-stone-800 dark:text-white mb-2">{t('home:estateRequest.title')}</h2>
          <p className="text-stone-600 dark:text-stone-300">{t('home:estateRequest.subtitle')}</p>
        </div>
        <div className="bg-white dark:bg-stone-800 rounded-2xl shadow-sm border border-amber-200 dark:border-stone-700 p-6 space-y-4">
          {localLeadStatus === 'success' ? (
            <div className="text-center py-6">
              <div className="text-5xl mb-3">🙏</div>
              <p className="text-xl font-bold text-green-700 dark:text-green-400">{t('home:estateRequest.successTitle')}</p>
              <p className="text-stone-600 dark:text-stone-300 mt-2">{t('home:estateRequest.successSubtitle')}</p>
            </div>
          ) : (
            <>
              <div>
                <label className="block text-base font-bold text-stone-700 dark:text-stone-200 mb-2">{t('home:estateRequest.typeLabel')}</label>
                <div className="space-y-2">
                  {REQUESTER_TYPE_OPTIONS.map(opt => (
                    <label
                      key={opt.value}
                      className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition ${
                        localLeadType === opt.value
                          ? 'border-[#C2410C] bg-orange-50 dark:bg-stone-800'
                          : 'border-stone-200 dark:border-stone-600 bg-white dark:bg-stone-800 hover:border-stone-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="localLeadType"
                        value={opt.value}
                        checked={localLeadType === opt.value}
                        onChange={e => setLocalLeadType(e.target.value as RequesterType)}
                        className="w-5 h-5 text-[#C2410C] focus:ring-[#C2410C]"
                      />
                      <div>
                        <p className="font-bold text-stone-800 dark:text-stone-100">{t(opt.labelKey)}</p>
                        <p className="text-sm text-stone-500 dark:text-stone-400">{t(opt.descKey)}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-base font-bold text-stone-700 dark:text-stone-200 mb-2">{t('home:estateRequest.estateNameLabel')}</label>
                <input
                  type="text"
                  value={localLeadEstate}
                  onChange={e => setLocalLeadEstate(e.target.value)}
                  placeholder={t('home:estateRequest.estateNamePlaceholder')}
                  className="w-full p-4 text-lg border border-stone-200 dark:border-stone-600 rounded-xl focus:ring-2 focus:ring-[#C2410C] focus:border-[#C2410C] outline-none transition bg-white dark:bg-stone-800 dark:text-white min-h-[56px]"
                />
              </div>
              <div>
                <label className="block text-base font-bold text-stone-700 dark:text-stone-200 mb-2">{t('home:estateRequest.districtLabel')}</label>
                <select
                  value={localLeadDistrict}
                  onChange={e => setLocalLeadDistrict(e.target.value)}
                  className="w-full p-4 text-lg border border-stone-200 dark:border-stone-600 rounded-xl focus:ring-2 focus:ring-[#C2410C] focus:border-[#C2410C] outline-none transition bg-white dark:bg-stone-800 dark:text-white min-h-[56px]"
                >
                  <option value="">{t('home:estateRequest.districtPlaceholder')}</option>
                  {HK_DISTRICT_GROUPS.map(g => (
                    <optgroup key={g.region} label={g.region}>
                      {g.areas.map(a => <option key={a} value={a}>{a}</option>)}
                    </optgroup>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-base font-bold text-stone-700 dark:text-stone-200 mb-2">{t('home:estateRequest.phoneLabel')}</label>
                <input
                  type="tel"
                  inputMode="tel"
                  value={localLeadPhone}
                  onChange={e => setLocalLeadPhone(e.target.value)}
                  placeholder={t('home:estateRequest.phonePlaceholder')}
                  className="w-full p-4 text-lg border border-stone-200 dark:border-stone-600 rounded-xl focus:ring-2 focus:ring-[#C2410C] focus:border-[#C2410C] outline-none transition bg-white dark:bg-stone-800 dark:text-white min-h-[56px]"
                />
              </div>
              {localLeadError && <p className="text-base text-red-600 font-bold">{localLeadError}</p>}
              <button
                type="button"
                onClick={handleLocalLeadSubmit}
                disabled={localLeadStatus === 'submitting'}
                className="w-full bg-[#C2410C] text-white text-lg font-bold py-4 rounded-xl hover:bg-[#EA580C] transition disabled:opacity-50 min-h-[56px]"
              >
                {localLeadStatus === 'submitting' ? t('home:estateRequest.submitting') : t('home:estateRequest.submit')}
              </button>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

interface ProductItem {
  id: string;
  name: string;
  nameEn?: string;
  description: string;
  descriptionEn?: string;
  price: number;
  imageUrl?: string;
  enabled: boolean;
  stockQuantity: number;
  category: string;
}

interface CartItem {
  productId: string;
  name: string;
  quantity: number;
  unitPrice: number;
  subtotal: number;
}

interface DeliveryInfo {
  address: string;
  addressBuilding: string;
  addressFloor: string;
  addressUnit: string;
  deliveryDate: string;
  estate: string;
  name: string;
  contactPhone: string;
  remarks: string;
  region: DeliveryRegion;
}

interface ServiceEstate {
  id: number;
  name: string;
  nameEn?: string;
  region: DeliveryRegion;
  district?: string;
  area?: string;
  estateType?: string;
  managementCompany?: string;
  minOrderAmount: number;
  deliveryDay?: string;
  supportsDelivery?: number;
  supportsPickup?: number;
  pickupLocation?: string;
  pickupTimeNote?: string;
}

const DEFAULT_PRODUCTS: ProductItem[] = [
  { id: 'i1', name: '日式清酒腐乳雞', nameEn: 'Japanese Sake Chicken with Fermented Tofu', description: '成分：雞、清酒、腐乳、薑蓉、蒜、生抽、鹽、糖、胡椒粉、蠔油｜420g', price: 49, imageUrl: '/dishes/i1.jpg', enabled: true, stockQuantity: 50, category: 'item' },
  { id: 'i2', name: '冬菇金針雲耳蒸雞', nameEn: 'Steamed Chicken with Mushrooms, Lily Buds & Cloud Ears', description: '成分：雞、冬菇、金針、雲耳、生抽、糖、麻油、胡椒粉、蠔油｜320g（雞肉嫩滑 原汁原味）', descriptionEn: 'Ingredients: chicken, shiitake mushrooms, lily buds, cloud ear fungus, light soy sauce, sugar, sesame oil, white pepper, oyster sauce | 320g (tender chicken, original flavour)', price: 49, imageUrl: '/dishes/i2.jpg', enabled: true, stockQuantity: 50, category: 'item' },
  { id: 'i3', name: '南乳蓮藕炆腩仔', nameEn: 'Braised Pork Belly & Lotus Root in Fermented Bean Curd Sauce', description: '成分：腩肉、蓮藕、南乳、醬油、冰糖｜700g', price: 59, imageUrl: '/dishes/i3.jpg', enabled: true, stockQuantity: 50, category: 'item' },
  { id: 'i4', name: '南洋風情牛脷丁', nameEn: 'Nanyang-Style Diced Beef Tongue', description: '成分：咖喱、牛脷丁、薯仔、紅薯、甘筍、洋蔥、菠蘿｜670g', price: 69, imageUrl: '/dishes/i4.jpg', enabled: true, stockQuantity: 50, category: 'item' },
  { id: 'i5', name: '洛神花豉油雞翼', nameEn: 'Roselle Soy-Glazed Chicken Wings', description: '成分：秘製豉油、雞翼、水、洛神花｜620g（8隻）', price: 49, imageUrl: '/dishes/i5.jpg', enabled: true, stockQuantity: 50, category: 'item' },
  { id: 'i6', name: '香芋椰汁雞', nameEn: 'Chicken & Taro in Coconut Sauce', description: '成分：雞球、芋頭、洋蔥、椰汁、花奶、魚露、醬油｜600g', price: 49, imageUrl: '/dishes/i6.jpg', enabled: true, stockQuantity: 50, category: 'item' },
  { id: 'i7', name: '無錫骨', nameEn: 'Wuxi-Style Spareribs', description: '成分：排骨粒、醬料｜350g', price: 49, enabled: true, stockQuantity: 50, category: 'item' },
  { id: 'i8', name: '黑椒洋蔥豬扒', nameEn: 'Black Pepper Pork Chops with Onion', description: '成分：豬扒、洋蔥、蒜頭、香草、黑椒碎、蠔油、醬油、糖｜700g', price: 49, imageUrl: '/dishes/i8.jpg', enabled: true, stockQuantity: 50, category: 'item' },
  { id: 'i9', name: '蒜肉麵醬南瓜炆雞球', nameEn: 'Braised Chicken & Pumpkin in Garlic Bean Sauce', description: '成分：蒜頭、甜麵醬、南瓜、雞球、豉油、鹽、糖、麻油、酒｜750g', price: 49, imageUrl: '/dishes/i9.jpg', enabled: true, stockQuantity: 50, category: 'item' },
  { id: 'i10', name: '蝦醬豆卜粉絲蒸腩片', nameEn: 'Steamed Pork Belly with Tofu Puffs, Glass Noodles & Shrimp Paste', description: '成分：腩片、豆卜、粉絲、蝦醬、薑蓉、生抽、鹽、糖、胡椒粉｜500g', price: 49, imageUrl: '/dishes/i10.jpg', enabled: true, stockQuantity: 50, category: 'item' },
  { id: 'i11', name: '蘋果燴豬扒', nameEn: 'Pork Chops Stewed with Apple', description: '成分：豬扒、蘋果、醬油、肉桂、香葉、酒｜700g', price: 59, imageUrl: '/dishes/i11.jpg', enabled: true, stockQuantity: 50, category: 'item' },
];

interface ApiProduct {
  id: number;
  category: string;
  name: string;
  name_en?: string | null;
  description: string | null;
  description_en?: string | null;
  price: number | null;
  is_active: number;
  stock_quantity: number;
  sort_order: number;
  image_url: string | null;
}

const CATEGORY_TABS: ('item' | 'vegetable' | 'fruit')[] = ['item', 'vegetable', 'fruit'];

function getSection<T extends HomePageSection['type']>(
  sections: HomePageSection[],
  type: T
): Extract<HomePageSection, { type: T }> | undefined {
  return sections.find((s): s is Extract<HomePageSection, { type: T }> => s.type === type);
}

function normalizeMerchantName(name?: string): string {
  if (!name) return '';
  return name.replace(/店店長/g, '').replace(/店長/g, '').replace(/店$/g, '').trim();
}

const OrderLanding: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const scenarioKey = searchParams.get('scenarioKey') || '';
  const referralCode = searchParams.get('gs');
  const gsCode = useMemo(
    () => {
      const urlGs = searchParams.get('gs');
      if (urlGs) return urlGs;
      // 若 URL 明確帶 estate 但不帶 gs，視為 B2C 直送 fallback，不採用 localStorage 殘留的商家/推薦碼
      if (searchParams.get('estate')) return undefined;
      return localStorage.getItem('gs_referral_code') || localStorage.getItem('gs2_referral_code') || undefined;
    },
    [searchParams]
  );
  const preselectedEstateFromUrl = searchParams.get('estate') || undefined;

  const heroDescription = [
    t('home:hero.desc1'),
    t('home:hero.desc2'),
    t('home:hero.desc3'),
  ].join('\n\n');

  const [step, setStep] = useState<'menu' | 'cart' | 'delivery'>('menu');

  const [productsByCategory, setProductsByCategory] = useState<Record<string, ProductItem[]>>({ item: DEFAULT_PRODUCTS });
  const [activeCategory, setActiveCategory] = useState<'item' | 'vegetable' | 'fruit'>('item');
  const [productPage, setProductPage] = useState(1);
  const PRODUCTS_PER_PAGE = 15;
  const [productsLoaded, setProductsLoaded] = useState(false);
  const [lowStockItems, setLowStockItems] = useState<Set<string>>(new Set());

  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [cart, setCart] = useState<CartItem[]>([]);

  const [delivery, setDelivery] = useState<DeliveryInfo>({
    address: '', addressBuilding: '', addressFloor: '', addressUnit: '', deliveryDate: '', estate: '', name: '', contactPhone: '', remarks: '', region: 'NT'
  });
  const [estates, setEstates] = useState<ServiceEstate[]>([]);
  const [minDateStr, setMinDateStr] = useState('');
  // 截單時間 / 送貨提示 / 自取設定（系統設置可調，public order-config 提供）
  const [orderConfig, setOrderConfig] = useState({
    cutoffTime: '20:00',
    deliveryTimeNote: '每日下午 4:00 前',
    masterDeliveryMinOrderAmount: 350,
    deliveryFee: 50,
    paymentProvider: 'bank_transfer',
    pickup: { location: '待定', timeNote: '每日 15:00-19:00', minOrderAmount: 0 },
    voucher: { earnThreshold: 150, redeemThreshold: 100, amount: 30 },
  });
  // 取貨方式：delivery 送貨上門 / pickup 自取；null 表示尚未選擇
  const [fulfillment, setFulfillment] = useState<'delivery' | 'pickup' | null>(null);
  // 未達最低消費時自動加入運費
  const [deliveryPhoneError, setDeliveryPhoneError] = useState('');
  const [voucherInfo, setVoucherInfo] = useState<{ count: number; amount: number } | null>(null);
  const [firstOrderInfo, setFirstOrderInfo] = useState<{ eligible: boolean; amount: number; minCart: number } | null>(null);
  const [featureFlags, setFeatureFlags] = useState<Record<string, boolean>>({
    first_order_discount_enabled: true,
    repurchase_voucher_enabled: true,
    referral_voucher_enabled: true,
    welcome_gift_enabled: true,
    egg_campaign_enabled: true,
    milestone_gift_enabled: true,
  });

  const [, setCampaignConfig] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  // 冪等鍵：一次結帳流程一把 key（提交失敗重試沿用同一把，後端見過即回放原單不重複建單）；
  // 元件卸載重掛（返回選餸再結帳、或下單成功後回上一頁）會換新 key，不影響正常重複下單
  const idempotencyKeyRef = useRef<string | null>(null);

  // 「其他屋苑」需求登記（lead capture）狀態
  const [leadName, setLeadName] = useState('');
  const [leadEstateName, setLeadEstateName] = useState('');
  const [leadDistrict, setLeadDistrict] = useState('');
  const [leadPhone, setLeadPhone] = useState('');
  const [leadType, setLeadType] = useState<RequesterType | ''>('');
  const [leadWebsite, setLeadWebsite] = useState(''); // honeypot，不顯示
  const [leadStatus, setLeadStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [leadError, setLeadError] = useState('');
  const [leadResult, setLeadResult] = useState<{ duplicate: boolean; estateCount: number; shareUrl: string | null } | null>(null);

  // 「其他屋苑」轉介鄰里店搜尋狀態
  const [otherEstateName, setOtherEstateName] = useState('');
  const [otherEstateMode, setOtherEstateMode] = useState<'search' | 'lead' | null>(null);

  const [homePage, setHomePage] = useState<HomePage | null>(null);
  const [leaderboard, setLeaderboard] = useState<Array<{ displayName: string; testimonial: string; directRefs: number }>>([]);
  const [leaderboardLoaded, setLeaderboardLoaded] = useState(false);

  // 鄰里店生態：商家資訊、屋苑查找、推薦
  const [merchantInfo, setMerchantInfo] = useState<{
    code: string;
    name: string;
    rating: number | null;
    reviewCount: number;
    minOrderAmount: number;
    estateMinOrderAmounts?: Record<string, number>;
    deliveryDays: number[];
    deliveryTime: string;
    deliveryCutoffTime: string;
    deliveryCutoffDaysBefore: number;
    isClosed: boolean;
    closedUntil: string;
    closedMessage: string;
    isBackup: boolean;
  } | null>(null);
  // Whether the customer has explicitly dismissed the estate finder prompt this session
  const [estateFinderPromptDismissed, setEstateFinderPromptDismissed] = useState(false);
  const [merchantEstateMap, setMerchantEstateMap] = useState<{ merchants: Record<string, any>; estateToMerchants: Record<string, string[]> } | null>(null);
  const [allServiceEstates, setAllServiceEstates] = useState<ServiceEstate[]>([]);
  const [estateSearchQuery, setEstateSearchQuery] = useState('');
  const [searchedEstate, setSearchedEstate] = useState('');
  const [estateSearchInput, setEstateSearchInput] = useState('');
  const [estateSearchResult, setEstateSearchResult] = useState<'current' | 'others' | 'none' | null>(null);
  const [showEstateFinder, setShowEstateFinder] = useState(false);
  const [showShowAndTell, setShowShowAndTell] = useState(false);
  const [estateFinderMode, setEstateFinderMode] = useState<'switch' | 'search'>('search');
  const [estatePreference, setEstatePreference] = useState<string>(() => {
    try { return localStorage.getItem('gs2_estate_preference') || ''; } catch { return ''; }
  });

  const referredByMerchantCode = searchParams.get('ref') || undefined;

  const cartTotal = cart.reduce((sum, item) => sum + item.subtotal, 0);
  // 折扣預覽：首次網購優先，其次回贈券（server 落單時先係最終決定）
  const firstOrderWillApply = featureFlags.first_order_discount_enabled && !!firstOrderInfo?.eligible && cartTotal >= (firstOrderInfo?.minCart || 100);
  const voucherWillApply = featureFlags.repurchase_voucher_enabled && !firstOrderWillApply && (voucherInfo?.count ?? 0) > 0 && cartTotal >= (orderConfig.voucher.redeemThreshold || 100);
  const previewDiscount = firstOrderWillApply
    ? (firstOrderInfo?.amount || 20)
    : (voucherWillApply ? (voucherInfo?.amount || 30) : 0);
  const allProducts = Object.values(productsByCategory).flat();
  const activeProducts = productsByCategory[activeCategory] || [];
  const visibleProducts = activeProducts.filter(p => p.enabled);
  const totalProductPages = Math.max(1, Math.ceil(visibleProducts.length / PRODUCTS_PER_PAGE));
  const safePage = Math.min(productPage, totalProductPages);
  const currentPageProducts = visibleProducts.slice((safePage - 1) * PRODUCTS_PER_PAGE, safePage * PRODUCTS_PER_PAGE);
  const menuTotal = allProducts.reduce((sum, p) => {
    const qty = quantities[p.id] || 0;
    return sum + qty * p.price;
  }, 0);
  const menuItemCount = allProducts.reduce((sum, p) => sum + (quantities[p.id] || 0), 0);
  const selectedEstate = estates.find(e => e.name === delivery.estate);
  const estateMinForMerchant = (m: any, estateName?: string) => {
    if (!m) return 0;
    if (estateName && m.estateMinOrderAmounts && typeof m.estateMinOrderAmounts[estateName] === 'number') {
      return Number(m.estateMinOrderAmounts[estateName]);
    }
    return Number(m.minOrderAmount ?? 0);
  };

  const BACKUP_MERCHANT_CODE = 'AY8HT';

  const isMerchantEffectiveClosed = (m: any): boolean => {
    if (!m?.isClosed) return false;
    const today = hktDateStr();
    return !m.closedUntil || today <= m.closedUntil;
  };

  const shouldShowBackupMerchant = (estateName: string): boolean => {
    if (!merchantEstateMap?.estateToMerchants?.[estateName]) return false;
    const codes = merchantEstateMap.estateToMerchants[estateName];
    const nonBackup = codes.filter(code => code !== BACKUP_MERCHANT_CODE);
    // 1. 該屋苑沒有其他鄰里店
    if (nonBackup.length === 0) return true;
    // 2. 所有其他鄰里店均休假
    const openCodes = nonBackup.filter(code => !isMerchantEffectiveClosed(merchantEstateMap.merchants[code]));
    if (openCodes.length === 0) return true;
    // 3. 其他開業中鄰里店未能覆蓋一星期 7 天
    const coveredDays = new Set<number>();
    for (const code of openCodes) {
      const days = merchantEstateMap.merchants[code]?.deliveryDays;
      if (Array.isArray(days)) days.forEach(d => coveredDays.add(d));
    }
    return coveredDays.size < 7;
  };

  const getVisibleMerchantCodes = (estateName: string, excludeCurrentCode?: string): string[] => {
    if (!merchantEstateMap?.estateToMerchants?.[estateName]) return [];
    let codes = merchantEstateMap.estateToMerchants[estateName].slice();
    const showBackup = shouldShowBackupMerchant(estateName);
    if (!showBackup) {
      codes = codes.filter(code => code !== BACKUP_MERCHANT_CODE);
    }
    if (excludeCurrentCode) {
      codes = codes.filter(code => code !== excludeCurrentCode);
    }
    // 備援店永遠排第一位
    codes.sort((a, b) => {
      if (a === BACKUP_MERCHANT_CODE) return -1;
      if (b === BACKUP_MERCHANT_CODE) return 1;
      const minA = estateMinForMerchant(merchantEstateMap.merchants[a], estateName);
      const minB = estateMinForMerchant(merchantEstateMap.merchants[b], estateName);
      if (!Number.isFinite(minA) || !Number.isFinite(minB)) return 0;
      return minA - minB;
    });
    return codes;
  };

  const filteredEstates = useMemo(() => {
    const q = estateSearchInput.trim().toLowerCase();
    if (!q) return [];
    return estates
      .filter(e =>
        e.name.toLowerCase().includes(q) ||
        (e.district && e.district.toLowerCase().includes(q)) ||
        (e.area && e.area.toLowerCase().includes(q))
      );
  }, [estateSearchInput, estates]);
  const estateMinOrderAmount = selectedEstate?.minOrderAmount || 0;
  // 無鄰里店覆蓋（其他屋苑）或一般 B2C 直送，均使用後台主最低消費
  const isDirectB2C = delivery.estate === OTHER_ESTATE || !merchantInfo;
  // 若已選擇鄰里店，優先使用該店對此屋苑的最低消費設置；0 視為有效值
  const merchantEstateMin = (merchantInfo && delivery.estate && delivery.estate !== OTHER_ESTATE)
    ? estateMinForMerchant(merchantInfo, delivery.estate)
    : null;
  // B2C 最低消費與後端一致：取「屋苑門檻」與「平台門檻」較大者（後端 Math.max 同步校驗）
  const masterDeliveryMin = (isDirectB2C && fulfillment !== 'pickup')
    ? Math.max(estateMinOrderAmount || 0, orderConfig.masterDeliveryMinOrderAmount || 0)
    : 0;
  const minOrderAmount = fulfillment === 'pickup'
    ? (merchantInfo ? 0 : (estateMinOrderAmount || orderConfig.pickup.minOrderAmount || 0))
    : (isDirectB2C
        ? masterDeliveryMin
        : (merchantEstateMin !== null ? merchantEstateMin : estateMinOrderAmount));
  const deliveryFee = (fulfillment !== 'pickup' && minOrderAmount > 0 && cartTotal < minOrderAmount) ? orderConfig.deliveryFee : 0;
  const payableTotal = cartTotal - previewDiscount + deliveryFee;
  const productShortfall = minOrderAmount > 0 ? Math.max(0, minOrderAmount - cartTotal) : 0;
  // 未達最低消費時自動加收運費：適用於所有 delivery（一般下單 + 鄰里店）
  const canUseDeliveryFee = fulfillment !== 'pickup' && minOrderAmount > 0 && cartTotal < minOrderAmount;
  // 是否已可結賬：達最低消費，或已自動加入運費
  const canProceed = productShortfall === 0 || canUseDeliveryFee;

  const isMerchantClosed = useMemo(() => {
    if (!merchantInfo) return false;
    const today = hktDateStr();
    return merchantInfo.isClosed && (!merchantInfo.closedUntil || today <= merchantInfo.closedUntil);
  }, [merchantInfo]);

  const closedMessageText = useMemo(() => {
    if (!isMerchantClosed || !merchantInfo) return '';
    return merchantInfo.closedMessage || (merchantInfo.closedUntil ? t('common:status.closedUntil', { date: merchantInfo.closedUntil }) : t('common:status.closedGeneric'));
  }, [isMerchantClosed, merchantInfo, i18n.language]);

  // 「其他屋苑」輸入時即時匹配可服務嘅鄰里店
  const otherEstateMatches = useMemo(() => {
    const name = otherEstateName.trim();
    if (!name || !merchantEstateMap?.merchants) return [];
    return getVisibleMerchantCodes(name)
      .map(code => ({ code, ...merchantEstateMap.merchants[code] }))
      .filter(m => m.code && m.name);
  }, [otherEstateName, merchantEstateMap]);

  const homeSections = homePage?.sections || DEFAULT_HOME_SECTIONS;
  const heroSection = getSection(homeSections, 'hero') || getSection(DEFAULT_HOME_SECTIONS, 'hero')!;
  const aboutSection = getSection(homeSections, 'about') || getSection(DEFAULT_HOME_SECTIONS, 'about');
  const footerSection = getSection(homeSections, 'footer') || getSection(DEFAULT_HOME_SECTIONS, 'footer');

  useEffect(() => {
    apiFetch('/api/public/order-config')
      .then(r => r.ok ? r.json() : null)
      .then(cfg => {
        if (cfg) {
          const pMin = Number(cfg.pickup?.minOrderAmount);
          const masterMin = Number(cfg.masterDeliveryMinOrderAmount);
          const deliveryFee = Number(cfg.deliveryFee);
          const vEarn = Number(cfg.voucher?.earnThreshold);
          const vRedeem = Number(cfg.voucher?.redeemThreshold);
          const vAmount = Number(cfg.voucher?.amount);
          setOrderConfig({
            cutoffTime: cfg.cutoffTime || '20:00',
            deliveryTimeNote: cfg.deliveryTimeNote || '每日下午 4:00 前',
            masterDeliveryMinOrderAmount: Number.isFinite(masterMin) && masterMin >= 0 ? masterMin : 350,
            deliveryFee: Number.isFinite(deliveryFee) && deliveryFee >= 0 ? deliveryFee : 50,
            paymentProvider: (cfg.paymentProvider || 'bank_transfer').trim() || 'bank_transfer',
            pickup: {
              location: cfg.pickup?.location || '待定',
              timeNote: cfg.pickup?.timeNote || '每日 15:00-19:00',
              minOrderAmount: Number.isFinite(pMin) && pMin >= 0 ? pMin : 0,
            },
            voucher: {
              earnThreshold: Number.isFinite(vEarn) && vEarn > 0 ? vEarn : 150,
              redeemThreshold: Number.isFinite(vRedeem) && vRedeem > 0 ? vRedeem : 100,
              amount: Number.isFinite(vAmount) && vAmount > 0 ? vAmount : 30,
            },
          });
        }
      })
      .catch(() => { /* 用預設值 */ });

    apiFetch('/api/public/feature-flags')
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.flags) {
          setFeatureFlags(prev => ({ ...prev, ...data.flags }));
        }
      })
      .catch(() => { /* 用預設值 */ });
  }, []);

  // 最早送貨日：截單前落單最早聽日，截單後順延一日；鄰里店則按店規則
  useEffect(() => {
    let minDateStr: string;
    if (merchantInfo) {
      minDateStr = getNextMerchantDeliveryDate(
        new Date(),
        merchantInfo.deliveryDays,
        merchantInfo.deliveryCutoffTime || orderConfig.cutoffTime,
        merchantInfo.deliveryCutoffDaysBefore || 1
      );
    } else {
      // B2C 截單一律以 HKT 計算（與後端 UTC+8 校驗一致；瀏覽器本地時區不可信）
      const [ch, cm] = orderConfig.cutoffTime.split(':').map(Number);
      const hktNow = new Date(Date.now() + 8 * 3600 * 1000);
      const beforeCutoff = (hktNow.getUTCHours() * 60 + hktNow.getUTCMinutes()) < (ch * 60 + (cm || 0));
      const minDate = new Date(Date.now() + 8 * 3600 * 1000 + (beforeCutoff ? 1 : 2) * 86400000);
      const y = minDate.getUTCFullYear();
      const m = String(minDate.getUTCMonth() + 1).padStart(2, '0');
      const d = String(minDate.getUTCDate()).padStart(2, '0');
      minDateStr = `${y}-${m}-${d}`;
    }
    setMinDateStr(minDateStr);
    // 預設/修正送貨日期：未揀或揀咗已過期嘅日期時，自動帶最早可送日
    setDelivery(prev => {
      if (!prev.deliveryDate || prev.deliveryDate < minDateStr) return { ...prev, deliveryDate: minDateStr };
      // 若已有日期但不屬於商家送貨日，自動跳到下一個送貨日
      if (merchantInfo?.deliveryDays?.length && !isMerchantDeliveryDay(prev.deliveryDate, merchantInfo.deliveryDays)) {
        return { ...prev, deliveryDate: minDateStr };
      }
      return prev;
    });
  }, [orderConfig.cutoffTime, merchantInfo]);

  // Load saved delivery info from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem('gs2Delivery');
      if (saved) {
        const parsed = JSON.parse(saved);
        // 若 URL 帶有預選屋苑（例如從其他屋苑轉介鄰里店），優先使用 URL 屋苑，勿用 localStorage 舊屋苑
        const savedEstate = preselectedEstateFromUrl ? '' : (parsed.estate || '');
        const savedRegion = parsed.region === 'HK' || parsed.region === 'KL' ? parsed.region : 'NT';
        const savedFulfillment = preselectedEstateFromUrl ? null : (parsed.fulfillment === 'delivery' || parsed.fulfillment === 'pickup' ? parsed.fulfillment : null);
        setDelivery(prev => ({
          ...prev,
          address: parsed.address || '',
          addressBuilding: parsed.addressBuilding || parsed.address || '',
          addressFloor: parsed.addressFloor || '',
          addressUnit: parsed.addressUnit || '',
          estate: savedEstate === OTHER_ESTATE ? '' : savedEstate,
          name: parsed.name || '',
          contactPhone: parsed.contactPhone || parsed.phone || '',
          remarks: parsed.remarks || '',
          region: savedRegion,
        }));
        setFulfillment(savedFulfillment);
      }
    } catch { /* ignore */ }
  }, []);

  // 自動記憶送貨資料（含方式），方便回頭客快速落單
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('gs2Delivery') || '{}');
      localStorage.setItem('gs2Delivery', JSON.stringify({
        ...saved,
        address: fulfillment === 'delivery' ? `${delivery.addressBuilding} ${delivery.addressFloor} ${delivery.addressUnit}`.trim() : '',
        addressBuilding: fulfillment === 'delivery' ? delivery.addressBuilding : '',
        addressFloor: fulfillment === 'delivery' ? delivery.addressFloor : '',
        addressUnit: fulfillment === 'delivery' ? delivery.addressUnit : '',
        estate: delivery.estate === OTHER_ESTATE ? '' : delivery.estate,
        region: delivery.region,
        name: delivery.name,
        contactPhone: delivery.contactPhone,
        remarks: delivery.remarks,
        fulfillment,
      }));
    } catch { /* ignore */ }
  }, [delivery.addressBuilding, delivery.addressFloor, delivery.addressUnit, delivery.estate, delivery.region, delivery.name, delivery.contactPhone, delivery.remarks, fulfillment]);

  useEffect(() => {
    if (scenarioKey) {
      apiFetch(`/api/public/campaigns/${scenarioKey}`)
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (data && data.config) {
            setCampaignConfig(data.config);
          }
        })
        .catch(() => { /* ignore */ });
    }
  }, [scenarioKey]);

  useEffect(() => {
    if (referralCode) {
      localStorage.setItem('gs_referral_code', referralCode);
      localStorage.setItem('gs2_referral_code', referralCode);
    }
  }, [referralCode]);

  // Track category tab changes as product/package views.
  useEffect(() => {
    if (CATEGORY_TABS.includes(activeCategory) && productsLoaded) {
      trackProductView(activeCategory, t(`home:shop.tab.${activeCategory}`));
    }
  }, [activeCategory, productsLoaded, t]);

  useEffect(() => {
    setProductPage(1);
  }, [activeCategory]);

  useEffect(() => {
    const url = gsCode
      ? `/api/public/service-estates?gs=${encodeURIComponent(gsCode)}`
      : '/api/public/service-estates';
    apiFetch(url)
      .then(r => r.ok ? r.json() : null)
      .then((data: any) => {
        if (!data) return;
        let loadedEstates: ServiceEstate[] = [];
        if (data.source === 'merchant') {
          setMerchantInfo({
            code: data.merchantCode,
            name: normalizeMerchantName(data.merchantName),
            rating: null,
            reviewCount: 0,
            minOrderAmount: Number(data.minOrderAmount || 0),
            estateMinOrderAmounts: Array.isArray(data.estates)
              ? Object.fromEntries(data.estates.map((e: any) => [e.name, Number(e.minOrderAmount ?? 0)]))
              : undefined,
            deliveryDays: Array.isArray(data.deliveryDays) ? data.deliveryDays : [],
            deliveryTime: data.deliveryTime || '',
            deliveryCutoffTime: data.deliveryCutoffTime || '',
            deliveryCutoffDaysBefore: Number(data.deliveryCutoffDaysBefore || 1),
            isClosed: !!data.isClosed,
            closedUntil: data.closedUntil || '',
            closedMessage: data.closedMessage || '',
            isBackup: false,
          });
          if (data.pickupLocation) {
            setOrderConfig(prev => ({
              ...prev,
              pickup: {
                ...prev.pickup,
                location: data.pickupLocation,
                timeNote: data.pickupTimeNote || prev.pickup.timeNote,
              },
            }));
          }
          if (Array.isArray(data.estates)) {
            loadedEstates = data.estates.sort((a: ServiceEstate, b: ServiceEstate) => (a.name || '').localeCompare(b.name || '', 'zh-HK'));
            setEstates(loadedEstates);
          }
        } else {
          setMerchantInfo(null);
          if (Array.isArray(data)) {
            loadedEstates = data.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'zh-HK'));
            setEstates(loadedEstates);
          }
        }
        // If a saved estate is no longer in the loaded list, clear it so the user can re-select
        if (delivery.estate && delivery.estate !== OTHER_ESTATE && !loadedEstates.some(e => e.name === delivery.estate)) {
          setDelivery(prev => ({ ...prev, estate: '', addressBuilding: '', addressFloor: '', addressUnit: '' }));
          setFulfillment(null);
          resetLeadState();
        }
      })
      .catch(() => { /* ignore */ });

    // 載入全域商家屋苑映射（用於 Hero 屋苑查找與互助推薦）
    apiFetch('/api/public/merchant-estate-map')
      .then(r => r.ok ? r.json() : null)
      .then((data: any) => {
        if (!data) return;
        // Normalize merchant names to handle 店長 -> 店
        if (data.merchants) {
          for (const code of Object.keys(data.merchants)) {
            if (data.merchants[code]?.name) {
              data.merchants[code].name = normalizeMerchantName(data.merchants[code].name);
            }
          }
        }
        setMerchantEstateMap(data);
        if (gsCode && data.merchants && data.merchants[gsCode]) {
          const m = data.merchants[gsCode];
          setMerchantInfo(prev => prev ? {
            ...prev,
            rating: m.rating,
            reviewCount: m.reviewCount,
            deliveryDays: Array.isArray(m.deliveryDays) ? m.deliveryDays : prev.deliveryDays,
            isClosed: typeof m.isClosed === 'boolean' ? m.isClosed : prev.isClosed,
            closedUntil: m.closedUntil ?? prev.closedUntil,
            isBackup: !!m.isBackup,
          } : null);
        }
      })
      .catch(() => { /* ignore */ });
  }, [gsCode]);

  // Estate finder modal 開啟時載入全部 service_estates，用於無 merchant 覆蓋屋苑的 autocomplete
  useEffect(() => {
    if (!showEstateFinder) return;
    apiFetch('/api/public/service-estates')
      .then(r => r.ok ? r.json() : null)
      .then((data: any) => {
        if (!data) return;
        const list: ServiceEstate[] = Array.isArray(data) ? data : (Array.isArray(data.estates) ? data.estates : []);
        setAllServiceEstates(list.sort((a: ServiceEstate, b: ServiceEstate) => (a.name || '').localeCompare(b.name || '', 'zh-HK')));
      })
      .catch(() => { /* ignore */ });
  }, [showEstateFinder]);

  // 當 localStorage 載入的 estate 與當前 estates 列表不同步時，清空 estate 避免畫面空白
  useEffect(() => {
    if (delivery.estate && delivery.estate !== OTHER_ESTATE && estates.length > 0 && !estates.some(e => e.name === delivery.estate)) {
      setDelivery(prev => ({ ...prev, estate: '', addressBuilding: '', addressFloor: '', addressUnit: '' }));
      setFulfillment(null);
      resetLeadState();
    }
  }, [delivery.estate, estates]);

  // 只有一個可服務屋苑時自動帶入，減少一次點擊
  useEffect(() => {
    if (estates.length === 1 && !delivery.estate) {
      const e = estates[0];
      setDelivery(prev => ({ ...prev, estate: e.name, region: e.region }));
      if (!merchantInfo) {
        setFulfillment('delivery');
      } else {
        const canDelivery = (e.supportsDelivery ?? 1) === 1;
        const canPickup = (e.supportsPickup ?? 0) === 1 && !!e.pickupLocation;
        setFulfillment(canDelivery && !canPickup ? 'delivery' : canPickup && !canDelivery ? 'pickup' : null);
      }
      resetLeadState();
    }
  }, [estates, delivery.estate, merchantInfo]);

  // 從 ?gs= 推薦連結進入且尚未選擇屋苑時，自動彈出屋苑選擇器
  useEffect(() => {
    if (!merchantInfo || estates.length <= 1 || delivery.estate || preselectedEstateFromUrl || showEstateFinder || estateFinderPromptDismissed) return;
    setShowEstateFinder(true);
  }, [merchantInfo, estates.length, delivery.estate, preselectedEstateFromUrl, showEstateFinder, estateFinderPromptDismissed]);

  // 從 URL ?estate=XXX 自動帶入屋苑（其他屋苑轉介鄰里店後用，或無覆蓋時進入 B2C 直送）
  useEffect(() => {
    if (preselectedEstateFromUrl && estates.length > 0 && !delivery.estate) {
      const e = estates.find(est => est.name === preselectedEstateFromUrl);
      if (e) {
        setDelivery(prev => ({ ...prev, estate: e.name, region: e.region }));
        if (!merchantInfo) {
          setFulfillment('delivery');
        } else {
          const canDelivery = (e.supportsDelivery ?? 1) === 1;
          const canPickup = (e.supportsPickup ?? 0) === 1 && !!e.pickupLocation;
          setFulfillment(canDelivery && !canPickup ? 'delivery' : canPickup && !canDelivery ? 'pickup' : null);
        }
        resetLeadState();
      } else {
        // 屋苑不在任何 merchant 服務範圍：進入 B2C 直送 fallback
        setDelivery(prev => ({ ...prev, estate: OTHER_ESTATE, region: 'NT' }));
        setOtherEstateName(preselectedEstateFromUrl);
        setFulfillment('delivery');
        resetLeadState();
      }
    }
  }, [preselectedEstateFromUrl, estates, delivery.estate, merchantInfo]);

  // 記住屋苑偏好：若無 URL 預選且尚未選擇屋苑，自動帶入上次選擇的屋苑（減少重複選擇）
  useEffect(() => {
    if (preselectedEstateFromUrl) return;
    if (delivery.estate || !estatePreference || estates.length === 0) return;
    const e = estates.find(est => est.name === estatePreference);
    if (!e) return;
    setDelivery(prev => ({ ...prev, estate: e.name, region: e.region }));
    if (!merchantInfo) {
      setFulfillment('delivery');
    } else {
      const canDelivery = (e.supportsDelivery ?? 1) === 1;
      const canPickup = (e.supportsPickup ?? 0) === 1 && !!e.pickupLocation;
      setFulfillment(canDelivery && !canPickup ? 'delivery' : canPickup && !canDelivery ? 'pickup' : null);
    }
  }, [preselectedEstateFromUrl, estates, estatePreference, delivery.estate, merchantInfo]);

  // 追蹤商家間推薦流量（?gs=YYY&ref=XXX）
  useEffect(() => {
    if (gsCode && referredByMerchantCode && gsCode !== referredByMerchantCode) {
      apiFetch('/api/public/track-merchant-referral-click', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ from: referredByMerchantCode, to: gsCode })
      }).catch(() => { /* ignore */ });
    }
  }, [gsCode, referredByMerchantCode]);

  // 回贈券查詢：顧客填寫聯絡電話後（debounce 500ms），安靜查詢是否有可用回贈券
  useEffect(() => {
    if (!featureFlags.repurchase_voucher_enabled) {
      setVoucherInfo(null);
      return;
    }
    const digits = delivery.contactPhone.replace(/\D/g, '');
    const normalized = digits.length === 8 ? `852${digits}` : digits;
    if (normalized.length < 10) {
      setVoucherInfo(null);
      return;
    }
    const timer = setTimeout(() => {
      apiFetch(`/api/public/voucher-lookup?phone=${normalized}`)
        .then(r => (r.ok ? r.json() : null))
        .then(data => {
          if (data && data.success && data.count > 0) {
            setVoucherInfo({ count: data.count, amount: Number(data.amount) || 30 });
          } else {
            setVoucherInfo(null);
          }
        })
        .catch(() => { /* 安靜失敗，不顯示任何提示 */ });
    }, 500);
    return () => clearTimeout(timer);
  }, [delivery.contactPhone]);

  // 首次網購減 $20 資格查詢（debounce 500ms）
  useEffect(() => {
    if (!featureFlags.first_order_discount_enabled) {
      setFirstOrderInfo(null);
      return;
    }
    const digits = delivery.contactPhone.replace(/\D/g, '');
    const normalized = digits.length === 8 ? `852${digits}` : digits;
    if (normalized.length < 10) {
      setFirstOrderInfo(null);
      return;
    }
    const timer = setTimeout(() => {
      const referralCode = localStorage.getItem('gs_referral_code') || searchParams.get('gs') || '';
      apiFetch(`/api/public/first-order-eligibility?phone=${normalized}&referralCode=${encodeURIComponent(referralCode)}`)
        .then(r => (r.ok ? r.json() : null))
        .then(data => {
          if (data && data.success && data.eligible) {
            setFirstOrderInfo({ eligible: true, amount: Number(data.amount) || 20, minCart: Number(data.minCart) || 100 });
          } else {
            setFirstOrderInfo(null);
          }
        })
        .catch(() => { /* 安靜失敗 */ });
    }, 500);
    return () => clearTimeout(timer);
  }, [delivery.contactPhone]);

  // 選擇「其他屋苑」時，lead form 的姓名/電話與送貨資料保持同步（預填 + 實時同步）
  useEffect(() => {
    if (delivery.estate === OTHER_ESTATE) {
      setLeadName(delivery.name);
      setLeadPhone(delivery.contactPhone);
    }
  }, [delivery.estate, delivery.name, delivery.contactPhone]);

  useEffect(() => {
    apiFetch('/api/public/products')
      .then(r => r.ok ? r.json() : null)
      .then((data: ApiProduct[] | null) => {
        if (data && Array.isArray(data)) {
          const lowStock = new Set<string>();
          const grouped: Record<string, ProductItem[]> = {};
          data
            .filter(p => p.is_active === 1)
            .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
            .forEach((p) => {
              if (p.stock_quantity > 0 && p.stock_quantity < 15) {
                lowStock.add(`i${p.id}`);
              }
              const item: ProductItem = {
                id: `i${p.id}`,
                name: p.name,
                nameEn: p.name_en || undefined,
                description: p.description || '',
                descriptionEn: p.description_en || undefined,
                price: p.price || 0,
                imageUrl: p.image_url || `/dishes/i${p.id}.jpg`,
                enabled: p.is_active === 1,
                stockQuantity: p.stock_quantity,
                category: p.category || 'item',
              };
              if (!grouped[item.category]) grouped[item.category] = [];
              grouped[item.category].push(item);
            });
          setProductsByCategory(prev => ({
            ...prev,
            ...grouped,
            item: grouped.item && grouped.item.length > 0 ? grouped.item : prev.item,
          }));
          setLowStockItems(lowStock);
        }
        setProductsLoaded(true);
      })
      .catch(() => { setProductsLoaded(true); });
  }, []);

  useEffect(() => {
    const fetchHome = async () => {
      try {
        const res = await apiFetch('/api/public/cms/pages/home');
        if (res.ok) {
          const data = await res.json();
          setHomePage({
            title: data.title || '好餸 Delicious Express',
            description: data.description || '',
            sections: Array.isArray(data.sections) && data.sections.length > 0
              ? data.sections
              : DEFAULT_HOME_SECTIONS,
            meta: data.meta || {},
            isActive: data.isActive ?? true,
          });
        } else {
          setHomePage({
            title: '好餸 Delicious Express',
            description: '',
            sections: DEFAULT_HOME_SECTIONS,
            meta: {},
            isActive: true,
          });
        }
      } catch {
        setHomePage({
          title: '好餸 Delicious Express',
          description: '',
          sections: DEFAULT_HOME_SECTIONS,
          meta: {},
          isActive: true,
        });
      }
    };
    const fetchLeaderboard = async () => {
      try {
        const res = await apiFetch('/api/public/leaderboard?limit=8');
        if (res.ok) {
          const data = await res.json();
          setLeaderboard(Array.isArray(data.items) ? data.items : []);
        }
      } catch (e) {
        console.error('[LEADERBOARD] Failed to load:', e);
      } finally {
        setLeaderboardLoaded(true);
      }
    };
    fetchHome();
    fetchLeaderboard();
  }, []);

  // 首次訪問自動顯示「好餸·鄰里」運作流程圖；客人可選「今日不再顯示」
  useEffect(() => {
    try {
      const hiddenUntil = localStorage.getItem('gs2_showtotell_hidden_until');
      const today = hktDateStr();
      if (hiddenUntil && hiddenUntil >= today) return;
    } catch { /* ignore */ }
    const timer = setTimeout(() => setShowShowAndTell(true), 800);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (homePage?.title) {
      document.title = `${cms('home.pageTitle', homePage.title)} | ${t('common:brand.suffix')}`;
    }
  }, [homePage?.title, i18n.language]);

  // 切換落單步驟後自動回到頁面頂部
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);

  const findProduct = (productId: string) => allProducts.find(p => p.id === productId);

  const handleQtyChange = (productId: string, delta: number) => {
    const product = findProduct(productId);
    if (delta > 0 && product) {
      trackAddToCart(productId, delta, product.price);
    }
    setQuantities(prev => {
      const current = prev[productId] || 0;
      const next = Math.max(0, current + delta);
      if (next === 0) {
        const { [productId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [productId]: next };
    });
  };

  const setQtyDirect = (productId: string, val: number) => {
    const product = findProduct(productId);
    const prevQty = quantities[productId] || 0;
    const v = Math.max(0, val);
    if (product && v > prevQty) {
      trackAddToCart(productId, v - prevQty, product.price);
    }
    setQuantities(prev => {
      if (v === 0) {
        const { [productId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [productId]: v };
    });
  };

  const buildCartFromQuantities = (): CartItem[] => {
    const items: CartItem[] = [];
    for (const p of allProducts) {
      const qty = quantities[p.id] || 0;
      if (qty > 0) {
        items.push({
          productId: p.id,
          name: p.name,
          quantity: qty,
          unitPrice: p.price,
          subtotal: p.price * qty,
        });
      }
    }
    return items;
  };

  const goToCart = () => {
    const items = buildCartFromQuantities();
    setCart(items);
    if (items.length > 0) {
      trackBeginCheckout(
        items.reduce((sum, i) => sum + i.subtotal, 0),
        items.reduce((sum, i) => sum + i.quantity, 0)
      );
    }
    setStep('cart');
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 0);
  };

  const handleGoToCart = () => {
    const items = buildCartFromQuantities();
    if (items.length === 0) return;
    // 必須先選定屋苑才能結賬
    if (!delivery.estate) {
      alert(t('home:finder.selectEstateFirst'));
      setEstateFinderMode('search');
      setShowEstateFinder(true);
      return;
    }
    goToCart();
  };

  const handleCartQtyChange = (productId: string, delta: number) => {
    if (delta > 0) {
      const product = findProduct(productId);
      if (product) {
        trackAddToCart(productId, delta, product.price);
      }
    }
    setQuantities(prev => {
      const current = prev[productId] || 0;
      const next = Math.max(0, current + delta);
      if (next === 0) {
        const { [productId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [productId]: next };
    });
  };

  const handleBackToMenu = () => {
    setStep('menu');
  };

  // 保持購物車與 quantities 同步，避免點擊購物車後出現空車
  useEffect(() => {
    setCart(buildCartFromQuantities());
  }, [quantities, productsByCategory]);

  const resetLeadState = () => {
    setLeadName('');
    setLeadEstateName('');
    setLeadDistrict('');
    setLeadPhone('');
    setLeadWebsite('');
    setLeadStatus('idle');
    setLeadError('');
    setLeadResult(null);
  };

  const handleDeliveryChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === 'deliveryDate') {
      const corrected = merchantInfo?.deliveryDays?.length
        ? getNextAllowedDeliveryDate(value, merchantInfo.deliveryDays)
        : value;
      setDelivery(prev => ({ ...prev, deliveryDate: corrected }));
      return;
    }
    if (name === 'estate') {
      const selected = estates.find(e => e.name === value);
      setDelivery(prev => ({
        ...prev,
        estate: value,
        region: selected && (selected.region === 'HK' || selected.region === 'KL' || selected.region === 'NT') ? selected.region : prev.region,
      }));
      // 切換屋苑後重置取貨方式，讓用戶根據屋苑支援的選項重新選擇
      setFulfillment(null);
      // 揀返服務名單內屋苑 → 重置「其他屋苑」搜尋及登記狀態
      // （揀「其他屋苑」時 lead 姓名/電話由 useEffect 同步初始化）
      if (value !== OTHER_ESTATE) {
        resetLeadState();
        setOtherEstateName('');
        setOtherEstateMode(null);
        saveEstatePreference(value);
      }
      return;
    }
    setDelivery(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setDeliveryPhoneError('');
    setSubmitError('');

    if (cart.length === 0) {
      setSubmitError(t('order:errors.noItems'));
      return;
    }
    // 未達最低消費不擋單：後端會自動加收運費（canUseDeliveryFee 情況下恆可提交），此處無需再擋
    if (!fulfillment) {
      setSubmitError(t('order:errors.noFulfillment'));
      return;
    }
    if (!delivery.estate) {
      setSubmitError(t('order:errors.noEstate'));
      return;
    }
    if (fulfillment === 'delivery') {
      if (!delivery.addressFloor || !delivery.addressUnit) {
        setSubmitError(t('order:errors.noFloorUnit'));
        return;
      }
    }

    const contactDigits = delivery.contactPhone.replace(/\D/g, '');
    if (contactDigits.length !== 8) {
      setDeliveryPhoneError(t('order:errors.invalidPhone'));
      return;
    }

    setIsSubmitting(true);

    const effectiveRefCode = gsCode;
    // 冪等鍵懶生成：首次提交時建立，重試沿用（後端回放原單）；成功後元件卸載自然作廢
    if (!idempotencyKeyRef.current) {
      idempotencyKeyRef.current = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `idem-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    }

    try {
      const orderItems = cart.map(item => ({
        productId: item.productId,
        name: item.name,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        subtotal: item.subtotal,
      }));

      const res = await apiFetch('/api/public/orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: orderItems,
          totalPrice: cartTotal,
          orderType: fulfillment,
          region: delivery.region,
          address: fulfillment === 'pickup' ? (selectedEstate?.pickupLocation || '') : `${delivery.addressBuilding} ${delivery.addressFloor} ${delivery.addressUnit}`.trim(),
          addressBuilding: fulfillment === 'delivery' ? delivery.addressBuilding : '',
          addressFloor: fulfillment === 'delivery' ? delivery.addressFloor : '',
          addressUnit: fulfillment === 'delivery' ? delivery.addressUnit : '',
          estate: delivery.estate === OTHER_ESTATE ? otherEstateName.trim() : delivery.estate,
          deliveryDate: delivery.deliveryDate,
          deliveryTime: '',
          name: delivery.name,
          phone: delivery.contactPhone,
          contactPhone: delivery.contactPhone,
          email: null,
          remarks: delivery.remarks,
          referralCode: effectiveRefCode,
          gsCode: effectiveRefCode,
          referredByMerchantCode: referredByMerchantCode,
          idempotencyKey: idempotencyKeyRef.current,
          language: i18n.language === 'en' ? 'en' : 'zh',
          campaignName: searchParams.get('scenarioKey') || 'gs2-default',
        })
      });

      if (res.ok) {
        const data = await res.json();
        const orderNum = (data.orderNum || "-");
        // 以 server 回傳嘅實際扣減為準（佢先係最終決定）；冇扣減時 payable = 原價
        const appliedDiscount = Number(data.voucherDiscount || 0);
        const payableAmount = typeof data.payable === 'number' ? data.payable : cartTotal - appliedDiscount;
        trackOrderCreated(orderNum, cartTotal);
        // Save delivery info (excluding date) for next order
        localStorage.setItem('gs2Delivery', JSON.stringify({
          address: fulfillment === 'delivery' ? `${delivery.addressBuilding} ${delivery.addressFloor} ${delivery.addressUnit}`.trim() : '',
          addressBuilding: fulfillment === 'delivery' ? delivery.addressBuilding : '',
          addressFloor: fulfillment === 'delivery' ? delivery.addressFloor : '',
          addressUnit: fulfillment === 'delivery' ? delivery.addressUnit : '',
          estate: delivery.estate === OTHER_ESTATE ? otherEstateName.trim() : delivery.estate,
          name: delivery.name,
          contactPhone: delivery.contactPhone,
          remarks: delivery.remarks,
          region: delivery.region,
          fulfillment,
        }));

        // KPay 全託管收銀台：建立訂單後直接跳轉付款（payment.provider = 'kpay' 時啟用；
        // 未配置 credentials 時 checkout 會回 503，fallback 到一般確認頁指示轉數快）
        if (orderConfig.paymentProvider === 'kpay') {
          try {
            const kpayRes = await apiFetch(`/api/public/orders/${encodeURIComponent(orderNum)}/kpay/checkout`, { method: 'POST' });
            const kpayData = kpayRes.ok ? await kpayRes.json().catch(() => null) : null;
            if (kpayRes.ok && kpayData?.payUrl) {
              window.location.href = kpayData.payUrl;
              return; // 跳轉中，唔好再 navigate
            }
            console.warn('[KPAY] checkout unavailable, falling back to manual payment page:', kpayData?.error || kpayRes.status);
          } catch (kpayErr) {
            console.warn('[KPAY] checkout error, falling back to manual payment page:', kpayErr);
          }
        }

        const deliveryFeeFromServer = Number(data.deliveryFee || 0);
        const discountParams = appliedDiscount > 0 ? `&discount=${appliedDiscount}&originalTotal=${cartTotal}` : '';
        const deliveryFeeParam = deliveryFeeFromServer > 0 ? `&deliveryFee=${deliveryFeeFromServer}` : '';
        const langParam = i18n.language === 'en' ? 'en' : 'zh';
        navigate(`/order/success?orderId=${data.id}&orderNum=${orderNum}&total=${payableAmount}${discountParams}${deliveryFeeParam}${data.referralCode ? '&refCode=' + data.referralCode : ''}&lang=${langParam}`);
      } else {
        const errorData = await res.json().catch(() => ({}));
        setSubmitError(t('order:errors.submitFailed', { status: res.status, error: errorData.error || t('order:errors.retryLater') }));
      }
    } catch (error) {
      setSubmitError(t('order:errors.network'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLeadSubmit = async () => {
    setLeadError('');
    if (!leadType) {
      setLeadError(t('home:estateRequest.errors.typeRequired'));
      return;
    }
    if (!leadEstateName.trim()) {
      setLeadError(t('home:estateRequest.errors.estateRequired'));
      return;
    }
    if (!leadDistrict) {
      setLeadError(t('home:estateRequest.errors.districtRequired'));
      return;
    }
    const digits = leadPhone.replace(/\D/g, '');
    const normalizedPhone = digits.length === 8 ? `852${digits}` : digits;
    if (!/^852\d{8}$/.test(normalizedPhone)) {
      setLeadError(t('home:estateRequest.errors.invalidPhone'));
      return;
    }
    setLeadStatus('submitting');
    try {
      const res = await apiFetch('/api/public/estate-requests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: leadName.trim() || undefined,
          phone: normalizedPhone,
          estateName: leadEstateName.trim(),
          district: leadDistrict,
          requesterType: leadType,
          referralCode: searchParams.get('gs') || localStorage.getItem('gs_referral_code') || localStorage.getItem('gs2_referral_code') || undefined,
          website: leadWebsite,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setLeadResult({
          duplicate: !!data.duplicate,
          estateCount: data.estateCount || 0,
          shareUrl: data.shareUrl || null,
        });
        setLeadStatus('success');
        trackEvent('estate_interest', { estateName: leadEstateName.trim(), district: leadDistrict });
      } else {
        setLeadStatus('error');
        setLeadError(data.error || t('order:errors.leadSubmitFailed'));
      }
    } catch {
      setLeadStatus('error');
      setLeadError(t('order:errors.network'));
    }
  };

  const handleLeadShare = () => {
    const shareUrl = leadResult?.shareUrl || 'https://shop.deliciousexpress.com.hk';
    const shareMsg = t('order:otherEstate.shareMessage', { url: shareUrl });
    window.open('https://wa.me/?text=' + encodeURIComponent(shareMsg), '_blank');
  };

  const handleLeadBack = () => {
    setDelivery(prev => ({ ...prev, estate: '' }));
    setOtherEstateName('');
    setOtherEstateMode(null);
    resetLeadState();
  };

  const handleBackFromDelivery = () => {
    if (delivery.estate === OTHER_ESTATE) {
      handleLeadBack();
    }
    goToCart();
  };

  const handleHeroCta = (url?: string) => {
    if (!url) return;
    if (url.startsWith('#')) {
      const id = url.slice(1);
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate(url);
    }
  };

  // 折扣提示：首次網購優先，其次回贈券（簡化顯示）
  const renderVoucherHint = () => {
    if (firstOrderWillApply) {
      return (
        <div className="mt-3 bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-900/30 dark:to-amber-900/30 border-2 border-orange-200 dark:border-orange-700 px-4 py-4 rounded-xl space-y-2">
          <div className="flex justify-between text-base text-stone-600 dark:text-gray-300">
            <span>{t('order:voucherHint.subtotal')}</span>
            <span>HK${cartTotal}</span>
          </div>
          <div className="flex justify-between text-base font-bold text-orange-700 dark:text-orange-400">
            <span>🎉 {t('order:voucherHint.firstOrderDiscount')}</span>
            <span>−HK${previewDiscount}</span>
          </div>
          <div className="flex justify-between text-lg font-bold text-stone-800 dark:text-white pt-2 border-t border-orange-200 dark:border-orange-700">
            <span>{t('order:voucherHint.payable')}</span>
            <span>HK${payableTotal}</span>
          </div>
        </div>
      );
    }
    if (!voucherInfo || voucherInfo.count <= 0 || !voucherWillApply) return null;
    return (
      <div className="mt-3 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 px-4 py-3 rounded-xl space-y-2">
        <div className="flex justify-between text-base text-stone-600 dark:text-gray-300">
          <span>{t('order:voucherHint.subtotal')}</span>
          <span>HK${cartTotal}</span>
        </div>
        <div className="flex justify-between text-base font-bold text-green-700 dark:text-green-400">
          <span>🎟️ {t('order:voucherHint.voucherAuto')}</span>
          <span>−HK${previewDiscount}</span>
        </div>
        <div className="flex justify-between text-lg font-bold text-stone-800 dark:text-white pt-2 border-t border-green-200 dark:border-green-700">
          <span>{t('order:voucherHint.payable')}</span>
          <span>HK${payableTotal}</span>
        </div>
      </div>
    );
  };

  // Checkout 進度條：選餸 → 我的訂單 → 填資料
  const renderCheckoutProgress = (currentStep: 'cart' | 'delivery') => {
    const steps = [
      { key: 'menu', label: t('order:progress.menu') },
      { key: 'cart', label: t('order:progress.cart') },
      { key: 'delivery', label: t('order:progress.delivery') },
    ];
    const activeIndex = currentStep === 'cart' ? 1 : 2;
    return (
      <div className="flex items-center justify-between mb-8">
        {steps.map((s, idx) => {
          const isActive = idx <= activeIndex;
          const isCurrent = idx === activeIndex;
          return (
            <React.Fragment key={s.key}>
              <div className="flex flex-col items-center gap-2">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center text-base font-bold shadow ${
                    isActive ? 'bg-[#C2410C] text-white' : 'bg-stone-200 text-stone-500'
                  } ${isCurrent ? 'ring-4 ring-orange-100' : ''}`}
                >
                  {idx + 1}
                </div>
                <span className={`text-sm font-bold ${isActive ? 'text-[#C2410C]' : 'text-stone-400'}`}>{s.label}</span>
              </div>
              {idx < steps.length - 1 && (
                <div className={`flex-1 h-1 mx-2 rounded ${idx < activeIndex ? 'bg-[#C2410C]' : 'bg-stone-200'}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  const handleEstateSearch = (estateName: string) => {
    const name = estateName.trim();
    setSearchedEstate(name);
    if (!name) {
      setEstateSearchResult(null);
      return;
    }
    if (merchantInfo && estates.some(e => e.name === name)) {
      setEstateSearchResult('current');
      return;
    }
    if (merchantEstateMap?.estateToMerchants && merchantEstateMap.estateToMerchants[name]) {
      setEstateSearchResult('others');
      return;
    }
    setEstateSearchResult('none');
  };

  const allEstateNames = useMemo(() => {
    if (!merchantEstateMap?.estateToMerchants) return [];
    return Object.keys(merchantEstateMap.estateToMerchants).sort((a, b) => a.localeCompare(b, 'zh-HK'));
  }, [merchantEstateMap]);

  const filteredEstateNames = useMemo(() => {
    const q = estateSearchQuery.trim();
    if (!q) return allEstateNames;
    return allEstateNames.filter(name => name.toLowerCase().includes(q.toLowerCase()));
  }, [estateSearchQuery, allEstateNames]);

  // 無 merchant 覆蓋但存在於 service_estates 的屋苑（用於 modal autocomplete 的 B2C fallback）
  const serviceOnlyMatches = useMemo(() => {
    const q = estateSearchQuery.trim();
    if (!q || !allServiceEstates.length) return [];
    return allServiceEstates
      .filter(e => e.name && !merchantEstateMap?.estateToMerchants[e.name])
      .filter(e => e.name.toLowerCase().includes(q.toLowerCase()))
      .map(e => e.name);
  }, [estateSearchQuery, allServiceEstates, merchantEstateMap]);

  const closeEstateFinder = () => {
    setShowEstateFinder(false);
    setEstateFinderPromptDismissed(true);
    setEstateSearchQuery('');
    setEstateSearchResult(null);
  };

  const saveEstatePreference = (name: string) => {
    try {
      if (name && name !== OTHER_ESTATE) {
        localStorage.setItem('gs2_estate_preference', name);
        setEstatePreference(name);
      }
    } catch { /* ignore */ }
  };

  const handlePreselectEstate = (name: string) => {
    const estate = estates.find(e => e.name === name);
    if (!estate) return;
    const canDelivery = (estate.supportsDelivery ?? 1) === 1;
    const canPickup = (estate.supportsPickup ?? 0) === 1 && !!estate.pickupLocation;
    const nextFulfillment = canDelivery && !canPickup ? 'delivery' : canPickup && !canDelivery ? 'pickup' : null;
    setDelivery(prev => ({
      ...prev,
      estate: estate.name,
      region: estate.region,
      addressBuilding: '',
      addressFloor: '',
      addressUnit: '',
    }));
    setFulfillment(nextFulfillment);
    resetLeadState();
    saveEstatePreference(estate.name);
    try {
      const saved = JSON.parse(localStorage.getItem('gs2Delivery') || '{}');
      localStorage.setItem('gs2Delivery', JSON.stringify({ ...saved, estate: estate.name, region: estate.region }));
    } catch { /* ignore */ }
    setShowEstateFinder(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSwitchMerchant = (code: string, estateName?: string) => {
    setShowEstateFinder(false);
    if (estateName) saveEstatePreference(estateName);
    const estateParam = estateName ? `&estate=${encodeURIComponent(estateName)}` : '';
    const refParam = merchantInfo?.code && merchantInfo.code !== code
      ? `&ref=${encodeURIComponent(merchantInfo.code)}`
      : '';
    // 延遲 navigate，讓 modal 完全卸載後才切換商家，避免 iPhone Safari
    // 在 modal backdrop-blur 與路由切換同時發生時出現白屏。
    setTimeout(() => {
      navigate(`/?gs=${encodeURIComponent(code)}${refParam}${estateParam}`, { replace: true });
      window.scrollTo({ top: 0, behavior: 'auto' });
    }, 200);
  };

  // 選擇一個存在於 service_estates 但無 merchant 覆蓋的屋苑：進入 B2C 直送 fallback
  const handleServiceOnlySelect = (name: string) => {
    try {
      localStorage.removeItem('gs_referral_code');
      localStorage.removeItem('gs2_referral_code');
      localStorage.removeItem('gs2_referral_ts');
    } catch { /* ignore */ }
    if (merchantInfo) {
      setShowEstateFinder(false);
      window.location.href = `/?estate=${encodeURIComponent(name)}`;
    } else {
      setShowEstateFinder(false);
      setDelivery(prev => ({ ...prev, estate: OTHER_ESTATE, region: 'NT' }));
      setOtherEstateName(name);
      setFulfillment('delivery');
      resetLeadState();
      handleHeroCta('#shop');
    }
  };

  const handleDontShowShowAndTellToday = () => {
    try {
      const today = hktDateStr();
      localStorage.setItem('gs2_showtotell_hidden_until', today);
    } catch { /* ignore */ }
  };

    const renderEstateFinderModal = () => {
    if (!showEstateFinder) return null;
    const isMerchant = !!merchantInfo;
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-2">
        <div
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={closeEstateFinder}
        />
        <div className="relative bg-white dark:bg-stone-900 rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden">
          <div className="relative flex flex-col items-start px-3 py-3">
            <button
              type="button"
              onClick={closeEstateFinder}
              className="absolute top-3 right-3 p-1 rounded-full hover:bg-stone-100 dark:hover:bg-stone-800 text-stone-500"
            >
              <X size={20} />
            </button>
            <div className="flex items-center gap-2 flex-wrap pr-8">
              {isMerchant && delivery.estate && delivery.estate !== OTHER_ESTATE && (
                <h2 className="text-lg md:text-xl font-bold text-stone-800 dark:text-white leading-tight">
                  {t('home:finder.selectedHeading', { estate: delivery.estate, merchant: merchantInfo.name })}
                </h2>
              )}
              {isMerchant && (!delivery.estate || delivery.estate === OTHER_ESTATE) && (
                <h2 className="text-lg md:text-xl font-bold text-stone-800 dark:text-white leading-tight">
                  {t('home:finder.storeHeading', { merchant: merchantInfo.name })}
                </h2>
              )}
            </div>
            {isMerchant && (selectedEstate ? estateMinForMerchant(merchantInfo, selectedEstate.name) : merchantInfo.minOrderAmount) > 0 && (
              <p className="mt-0.5 text-sm text-stone-600 dark:text-stone-300 pr-8">
                {t('home:finder.freeDeliveryMin', { amount: selectedEstate ? estateMinForMerchant(merchantInfo, selectedEstate.name) : merchantInfo.minOrderAmount })}

              </p>
            )}
          </div>

          <div className="pt-1 pb-2 px-3 space-y-3 overflow-y-auto">
            {estateFinderMode === 'search' && isMerchant && delivery.estate && delivery.estate !== OTHER_ESTATE && (() => {
              const otherCodes = (merchantEstateMap?.estateToMerchants[delivery.estate] || [])
                .filter(code => code !== merchantInfo?.code);
              if (otherCodes.length === 0) return null;
              return (
                <button
                  type="button"
                  onClick={() => {
                    setEstateSearchQuery(delivery.estate);
                    setSearchedEstate(delivery.estate);
                    setEstateSearchResult('others');
                  }}
                  className="inline-flex items-center gap-1.5 text-xs font-bold text-orange-900 bg-orange-200 px-2.5 py-1 rounded-full border border-orange-300 hover:bg-orange-300 transition"
                >
                  {t('home:finder.otherStoresInEstate')}
                </button>
              );
            })()}

            {estateFinderMode === 'switch' && isMerchant && delivery.estate && delivery.estate !== OTHER_ESTATE && (
              <div className="space-y-3">
                {(() => {
                  const currentMin = estateMinForMerchant(merchantInfo, delivery.estate);
                  const otherCodes = getVisibleMerchantCodes(delivery.estate, merchantInfo?.code);
                  return (
                    <>
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-bold text-stone-700 dark:text-stone-200">
                          {t('home:finder.currentStoreIntroPrefix')} <span className="text-[#C2410C]">{delivery.estate}</span> {t('home:finder.currentStoreIntroSuffix')}
                        </p>
                      </div>
                      <div className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-orange-50 dark:bg-stone-800 border border-[#C2410C] dark:border-stone-600 rounded-xl text-stone-800 dark:text-stone-100 text-left">
                        <div>
                          <p className="font-bold text-base">{t('home:finder.storeName', { name: merchantInfo.name })} <span className="text-xs font-normal text-[#C2410C]">{t('home:finder.selectedTag')}</span></p>
                          <p className="text-xs text-stone-500 dark:text-stone-400 mt-0.5">
                            {merchantInfo.deliveryDays?.length === 7
                              ? t('home:finder.dailyDelivery')
                              : merchantInfo.deliveryDays?.length
                                ? t('home:finder.deliveryDaysOn', { days: formatDeliveryDays(merchantInfo.deliveryDays) })
                                : t('home:finder.noDeliveryDays')}
                          </p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-xs text-stone-500 dark:text-stone-400">{t('home:finder.freeShippingMinLabel')}</p>
                          <p className="font-bold text-[#C2410C]">HK${currentMin}</p>
                        </div>
                      </div>
                      {otherCodes.length > 0 ? (
                        <>
                          <p className="text-sm font-bold text-stone-700 dark:text-stone-200">
                            {t('home:finder.switchPrefix')}<span className="text-[#C2410C]">{t('home:finder.storeWord')}</span>{t('home:finder.switchSuffix')}
                          </p>
                          {otherCodes.map(code => {
                            const m = merchantEstateMap?.merchants[code];
                            if (!m) return null;
                            return (
                              <button
                                key={code}
                                type="button"
                                onClick={() => handleSwitchMerchant(code, delivery.estate)}
                                className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-600 rounded-xl text-stone-800 dark:text-stone-100 hover:border-[#C2410C] hover:bg-orange-50 dark:hover:bg-stone-700 transition text-left"
                              >
                                <div>
                                  <p className="font-bold text-base">{m.isBackup ? t('home:finder.backupStoreName', { name: m.name }) : t('home:finder.storeName', { name: m.name })}</p>
                                  <p className="text-xs text-stone-500 dark:text-stone-400 mt-0.5">
                                    {m.deliveryDays?.length === 7
                                      ? t('home:finder.dailyDelivery')
                                      : m.deliveryDays?.length
                                        ? t('home:finder.deliveryDaysOn', { days: formatDeliveryDays(m.deliveryDays) })
                                        : t('home:finder.noDeliveryDays')}
                                  </p>
                                </div>
                                <div className="text-right shrink-0">
                                  <p className="text-xs text-stone-500 dark:text-stone-400">{t('home:finder.freeShippingMinLabel')}</p>
                                  <p className="font-bold text-[#C2410C]">HK${estateMinForMerchant(m, delivery.estate)}</p>
                                </div>
                              </button>
                            );
                          })}
                        </>
                      ) : (
                        <p className="text-sm text-stone-500 dark:text-stone-400">{t('home:finder.onlyStore', { name: merchantInfo.name })}</p>
                      )}
                      <button
                        type="button"
                        onClick={() => setEstateFinderMode('search')}
                        className="w-full py-2 text-sm font-bold text-[#C2410C] border border-[#C2410C] rounded-lg hover:bg-orange-50 dark:hover:bg-stone-800 transition"
                      >
                        {t('home:finder.searchAgain')}
                      </button>
                    </>
                  );
                })()}
              </div>
            )}

            {estateFinderMode === 'search' && (
              <>
                <div>
                  <p className="inline-flex items-center gap-1.5 text-xs font-bold text-black mb-1">
                    <MapPin size={13} />
                    {t('home:finder.searchAllLabel')}
                  </p>
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" size={20} />
                    <input
                      type="text"
                      autoFocus
                      value={estateSearchQuery}
                      onChange={(e) => {
                        setEstateSearchQuery(e.target.value);
                        if (e.target.value.trim()) handleEstateSearch(e.target.value);
                        else setEstateSearchResult(null);
                      }}
                      placeholder={t('home:finder.inputPlaceholder')}
                      className="w-full pl-12 pr-4 py-3 text-lg border border-stone-200 dark:border-stone-600 rounded-xl focus:ring-2 focus:ring-[#C2410C] focus:border-[#C2410C] outline-none bg-white dark:bg-stone-800 dark:text-white"
                    />
                  </div>
                </div>

                {estateSearchQuery.trim() && (
                  <div className="space-y-3">
                    <>
                      {estateSearchResult === 'current' && (
                        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4 text-left">
                          {(() => {
                            const otherMerchants = getVisibleMerchantCodes(searchedEstate, merchantInfo?.code);
                            return (
                              <>
                                <p className="text-base font-bold text-green-800 dark:text-green-200 mb-1">{t('home:finder.inServiceArea', { estate: searchedEstate })}</p>
                                {null}
                                <div className="flex flex-wrap gap-2">
                                  <button
                                    type="button"
                                    onClick={() => handlePreselectEstate(searchedEstate)}
                                    disabled={isMerchantClosed}
                                    className={`px-4 py-2 text-white text-sm font-bold rounded-lg transition ${
                                      isMerchantClosed
                                        ? 'bg-stone-400 cursor-not-allowed'
                                        : 'bg-green-600 hover:bg-green-700'
                                    }`}
                                  >
                                    {isMerchantClosed ? t('common:status.storeClosed') : t('common:actions.shopNow')}
                                  </button>
                                  {otherMerchants.length > 0 && (
                                    <button
                                      type="button"
                                      onClick={() => setEstateSearchResult('others')}
                                      className="px-4 py-2 text-sm font-bold rounded-lg border border-green-600 text-green-700 hover:bg-green-100 dark:text-green-300 dark:border-green-500 dark:hover:bg-green-900/30 transition"
                                    >
                                      {t('home:finder.viewOthers', { count: otherMerchants.length })}
                                    </button>
                                  )}
                                </div>
                              </>
                            );
                          })()}
                        </div>
                      )}
                      {estateSearchResult === 'others' && (
                        <div className="space-y-2">
                          <p className="text-sm font-bold text-stone-700 dark:text-stone-200">
                            {t('home:finder.choosePrefix')}<span className="text-[#C2410C]">{t('home:finder.storeWord')}</span>{t('home:finder.chooseSuffix')}
                          </p>
                          {getVisibleMerchantCodes(searchedEstate, merchantInfo?.code)
                            .map(code => {
                              const m = merchantEstateMap?.merchants[code];
                              if (!m) return null;
                              return (
                                <button
                                  key={code}
                                  type="button"
                                  onClick={() => handleSwitchMerchant(code, searchedEstate)}
                                  className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-600 rounded-xl text-stone-800 dark:text-stone-100 hover:border-[#C2410C] hover:bg-orange-50 dark:hover:bg-stone-700 transition text-left"
                                >
                                  <div>
                                    <p className="font-bold text-base">{m.isBackup ? t('home:finder.backupStoreName', { name: m.name }) : t('home:finder.storeName', { name: m.name })}</p>
                                    <p className="text-xs text-stone-500 dark:text-stone-400 mt-0.5">
                                    {m.deliveryDays?.length === 7
                                      ? t('home:finder.dailyDelivery')
                                      : m.deliveryDays?.length
                                        ? t('home:finder.deliveryDaysOn', { days: formatDeliveryDays(m.deliveryDays) })
                                        : t('home:finder.noDeliveryDays')}
                                  </p>
                                  </div>
                                  <div className="text-right shrink-0">
                                    <p className="text-xs text-stone-500 dark:text-stone-400">{t('home:finder.freeShippingMinLabel')}</p>
                                    <p className="font-bold text-[#C2410C]">HK${estateMinForMerchant(m, searchedEstate)}</p>
                                  </div>
                                </button>
                              );
                            })}
                        </div>
                      )}
                      {estateSearchResult !== 'current' && estateSearchResult !== 'others' && (
                        <>
                          {filteredEstateNames.length > 0 && (
                            <div>
                              <p className="text-sm font-bold text-stone-700 dark:text-stone-200 mb-2">{t('home:finder.matchedEstates')}</p>
                              <div className="flex flex-wrap gap-2">
                                {filteredEstateNames.map(name => {
                                  return (
                                    <button
                                      key={name}
                                      type="button"
                                      onClick={() => {
                                        setEstateSearchQuery(name);
                                        handleEstateSearch(name);
                                        saveEstatePreference(name);
                                      }}
                                      className="px-3 py-2 bg-stone-50 dark:bg-stone-800 text-stone-700 dark:text-stone-200 text-sm font-medium rounded-lg hover:bg-orange-50 dark:hover:bg-stone-700 border border-stone-200 dark:border-stone-600 transition"
                                    >
                                      {name}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          {serviceOnlyMatches.length > 0 && (
                            <div>
                              <p className="text-sm font-bold text-stone-700 dark:text-stone-200 mb-2">{t('home:finder.serviceOnlyIntro')}</p>
                              <div className="flex flex-wrap gap-2">
                                {serviceOnlyMatches.map(name => (
                                  <button
                                    key={name}
                                    type="button"
                                    onClick={() => handleServiceOnlySelect(name)}
                                    className="px-3 py-2 bg-amber-50 dark:bg-amber-900/20 text-stone-700 dark:text-stone-200 text-sm font-medium rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/40 border border-amber-200 dark:border-amber-800 transition"
                                  >
                                    {name}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                          {filteredEstateNames.length === 0 && serviceOnlyMatches.length === 0 && (
                            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4 text-left">
                              <p className="text-base font-bold text-amber-800 dark:text-amber-200 mb-1">{t('home:finder.notCovered', { estate: estateSearchQuery.trim() })}</p>
                              <p className="text-sm text-amber-700 dark:text-amber-300 mb-3">{t('home:finder.leaveInfo')}</p>
                              <button
                                type="button"
                                onClick={() => { closeEstateFinder(); handleHeroCta('#estate-request'); }}
                                className="px-4 py-2 bg-[#C2410C] text-white text-sm font-bold rounded-lg hover:bg-[#EA580C] transition"
                              >
                                {t('home:finder.registerEstate')}
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderHero = (s: HeroSection) => (
    <section className="relative min-h-[80vh] md:min-h-screen flex flex-col overflow-hidden">
      <div className="relative flex-1 flex items-center justify-center">
        <div className="absolute inset-0">
          {/* Soft beige fallback — always rendered so there is never a blank/broken background */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#F9F6F0] via-[#F2EEE6] to-[#EBE5DB]" />
          {s.backgroundImage && (
            <div
              className="absolute inset-0 bg-cover bg-center bg-no-repeat z-[1]"
              style={{ backgroundImage: `url("${s.backgroundImage}")` }}
            />
          )}
          <div className="absolute inset-0 bg-black/50 z-[2]" />
        </div>

        {/* Hero Estate Finder — header 底線 + 8px */}
        <div className="absolute top-[3.75rem] left-0 right-0 z-[6] px-4">
          <div className="max-w-2xl mx-auto">
            <button
              type="button"
              onClick={() => {
                setEstateFinderMode('search');
                setShowEstateFinder(true);
              }}
              className="w-full flex items-center gap-3 pl-4 pr-4 py-3 text-left text-lg bg-white/95 backdrop-blur rounded-xl border border-stone-200 shadow-lg hover:bg-white transition"
            >
              <Search className="text-stone-400 shrink-0" size={20} />
              <span className="text-stone-500 truncate">
                {delivery.estate && delivery.estate !== OTHER_ESTATE
                  ? t('home:finder.selectedBar', {
                      estate: delivery.estate,
                      store: merchantInfo
                        ? t('home:finder.storeName', { name: merchantInfo.name.replace(/店長/g, '').replace(/\s+/g, '') })
                        : t('home:finder.officialDirect'),
                    })
                  : delivery.estate === OTHER_ESTATE && otherEstateName.trim()
                    ? t('home:finder.selectedBar', { estate: otherEstateName.trim(), store: t('home:finder.officialDirect') })
                    : t('home:finder.inputPlaceholder')}
              </span>
            </button>
          </div>
        </div>

        <div className="relative z-[5] max-w-5xl mx-auto px-4 pt-32 pb-12 md:pt-40 md:pb-20 text-center text-white">
          <p className="text-brand-100 text-sm md:text-base font-medium mb-4 tracking-wider">
            {t('home:hero.kicker')}
          </p>
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
            <span dangerouslySetInnerHTML={{ __html: cms('home.hero.title', s.title) }} />
          </h1>
          <div className="text-base md:text-lg text-white/90 leading-relaxed mb-4 max-w-2xl mx-auto">
            {renderDescription(heroDescription)}
          </div>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <button
              onClick={() => {
                if (isMerchantClosed) return;
                if (merchantInfo && !delivery.estate) {
                  setShowEstateFinder(true);
                  return;
                }
                handleHeroCta(s.ctaPrimary.url);
              }}
              disabled={isMerchantClosed}
              className={`inline-flex items-center gap-2 px-8 py-3.5 font-bold rounded-full transition-colors shadow-lg ${
                isMerchantClosed
                  ? 'bg-stone-500 text-white cursor-not-allowed'
                  : 'bg-[#C2410C] hover:bg-[#9a3412] text-white'
              }`}
            >
              <ShoppingCart size={18} />
              {isMerchantClosed ? t('common:status.storeClosed') : cms('home.hero.ctaPrimary', s.ctaPrimary.label || t('home:hero.ctaPrimaryFallback'))}
            </button>
            <button
              onClick={() => handleHeroCta(s.ctaSecondary.url)}
              className="inline-flex items-center gap-2 px-8 py-3.5 border-2 border-white/80 text-white font-bold rounded-full hover:bg-white/10 transition-colors"
            >
              {cms('home.hero.ctaSecondary', s.ctaSecondary.label || t('home:hero.ctaSecondaryFallback'))}
            </button>
          </div>
          <button
            type="button"
            onClick={() => setShowShowAndTell(true)}
            className="mt-5 inline-flex items-center gap-1.5 text-white/90 hover:text-white text-sm font-medium underline underline-offset-4 hover:bg-white/10 px-3 py-1.5 rounded-full transition"
          >
            <Users size={14} />
            {t('home:hero.howItWorks')}
          </button>
        </div>
      </div>
    </section>
  );

  const renderLeaderboard = () => {
    if (!leaderboardLoaded || leaderboard.length < 5) return null;
    return (
      <section className="py-12 md:py-16 px-4 bg-[#FFF7ED]">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 text-[#C2410C] font-medium mb-3">
              <Users size={20} />
              <span>{t('home:leaderboard.title')}</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-stone-800 mb-2">
              {t('home:leaderboard.heading')}
            </h2>
            <p className="text-stone-600">
              {t('home:leaderboard.subheading')}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {leaderboard.slice(0, 8).map((item, idx) => (
              <div
                key={idx}
                className="bg-white rounded-2xl p-5 border border-orange-100 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#C2410C] to-[#F59E0B] flex items-center justify-center text-white font-bold text-sm">
                    {item.displayName.charAt(0)}
                  </div>
                  <div>
                    <p className="font-bold text-stone-800 text-sm">{item.displayName}</p>
                    <p className="text-xs text-[#C2410C] font-medium">{t('home:leaderboard.influenced', { count: item.directRefs })}</p>
                  </div>
                </div>
                {item.testimonial && (
                  <p className="text-stone-600 text-sm leading-relaxed italic">
                    「{item.testimonial}」
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  const renderAbout = (s?: AboutSection) => {
    if (!s) return null;
    return (
      <section className="py-16 md:py-24 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div className="order-2 lg:order-1">
              <div className="inline-flex items-center gap-2 text-[#C2410C] font-medium mb-4">
                <Users size={20} />
                <span>{t('home:about.kicker')}</span>
              </div>
              <h2 className="text-2xl md:text-4xl font-bold text-stone-800 mb-6">{cms('home.about.title', s.title)}</h2>
              <div className="text-stone-600 leading-relaxed mb-8">
                {renderDescription(cms('home.about.description', s.description))}
              </div>
              <button
                onClick={() => navigate('/about')}
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#C2410C] text-white font-bold rounded-full hover:bg-[#9a3412] transition-colors shadow-lg"
              >
                {t('home:about.learnMore')}
                <ChevronRight size={18} />
              </button>
            </div>
            <div className="order-1 lg:order-2">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-xl bg-stone-100">
                {s.imageUrl ? (
                  <img
                    src={s.imageUrl}
                    alt={s.imageAlt}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-stone-400">
                    <ChefHat size={48} />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  };

  const renderWhatsAppCta = () => (
    <section className="py-16 md:py-24 px-4 bg-[#FFF7ED]">
      <div className="max-w-4xl mx-auto text-center">
        <div className="w-16 h-16 bg-[#25D366] rounded-full flex items-center justify-center mx-auto mb-6 text-white shadow-lg">
          <MessageCircle size={32} />
        </div>
        <h2 className="text-2xl md:text-4xl font-bold text-stone-800 mb-4">
          {t('home:whatsappCta.title')}
        </h2>
        <p className="text-stone-600 mb-8">
          {t('home:whatsappCta.subtitle')}
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href="https://wa.me/85298536993"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackWhatsAppClick(undefined, 'cta')}
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#25D366] text-white font-bold rounded-full hover:bg-[#128C7E] transition-colors shadow-lg"
          >
            <Phone size={18} />
            WhatsApp 9853 6993
          </a>
          <a
            href="tel:+85235946130"
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-white text-stone-700 font-bold rounded-full border border-stone-200 hover:bg-stone-50 transition-colors"
          >
            <Phone size={18} />
            {t('home:whatsappCta.call')}
          </a>
        </div>
      </div>
    </section>
  );


  return (
    <div className="min-h-screen bg-[#FFFBF5] font-sans text-stone-800 pb-32">
      {/* ===== Header ===== */}
      <SiteHeader
        cartCount={menuItemCount}
        onCartClick={goToCart}
        hideNav={step !== 'menu'}
      />

      <ShowAndTellModal
        isOpen={showShowAndTell}
        onClose={() => setShowShowAndTell(false)}
        onDontShowAgain={handleDontShowShowAndTellToday}
      />

      {renderEstateFinderModal()}

      {/* ===== Brand + Shop Landing (menu step) ===== */}
      {step === 'menu' && (
        <>
          {renderHero(heroSection)}
          {/* ===== 優惠推廣橫幅 ===== */}
          {(featureFlags.first_order_discount_enabled || featureFlags.repurchase_voucher_enabled) && (
            <div className="bg-gradient-to-r from-orange-50 via-amber-50 to-orange-50 dark:from-orange-900/30 dark:via-amber-900/30 dark:to-orange-900/30 border-b border-orange-100 dark:border-orange-800">
              <div className="max-w-5xl mx-auto px-4 py-3 text-center text-sm md:text-base font-bold text-orange-800 dark:text-orange-200 space-y-1">
                {featureFlags.first_order_discount_enabled && (
                  <p>{(searchParams.get('gs') || localStorage.getItem('gs_referral_code')) ? t('home:banner.firstOrderReferred') : t('home:banner.firstOrder')}</p>
                )}
                {featureFlags.repurchase_voucher_enabled && (
                  <p className="text-xs md:text-sm font-medium text-orange-700 dark:text-orange-300">{t('home:banner.voucher', { earn: orderConfig.voucher.earnThreshold, amount: orderConfig.voucher.amount, redeem: orderConfig.voucher.redeemThreshold })}</p>
                )}
              </div>
            </div>
          )}
          {renderAbout(aboutSection)}

          {/* ===== 好餸傳愛榜 ===== */}
          {renderLeaderboard()}

          {/* ===== Product Shop ===== */}
          <section id="shop" className="py-8 px-4 scroll-mt-24">
            <div className="max-w-5xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-[#C2410C] rounded-full flex items-center justify-center text-white text-lg font-bold shadow-lg">1</div>
                <div>
                  <h3 className="text-2xl font-bold text-stone-800">{t('home:shop.title')}</h3>
                  <p className="text-sm text-stone-500">{t('home:shop.subtitle')}</p>
                </div>

              </div>

              {isMerchantClosed && (
                <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-xl text-red-800 dark:text-red-200 text-base font-bold">
                  🚫 {closedMessageText}
                </div>
              )}

              {!productsLoaded ? (
                <div className="text-center py-16">
                  <Loader2 className="animate-spin mx-auto text-[#C2410C]" size={40} />
                  <p className="mt-4 text-stone-400">{t('home:shop.loading')}</p>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-4 mb-6 border-b border-stone-200">
                    {CATEGORY_TABS.map(key => (
                      <button
                        key={key}
                        onClick={() => setActiveCategory(key)}
                        className={`pb-2.5 px-2 text-base font-bold border-b-2 transition min-h-[44px] ${
                          activeCategory === key
                            ? 'text-orange-600 border-orange-600'
                            : 'text-gray-500 border-transparent'
                        }`}
                      >
                        {t(`home:shop.tab.${key}`)}
                      </button>
                    ))}
                  </div>

                  {visibleProducts.length === 0 ? (
                    <div className="text-center py-16 text-stone-400">{t('home:shop.noProducts')}</div>
                  ) : (
                    <>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                      {currentPageProducts.map(product => {
                        const qty = quantities[product.id] || 0;
                        const isLowStock = lowStockItems.has(product.id);
                        const soldOut = product.stockQuantity <= 0;
                        return (
                          <div
                            key={product.id}
                            className={`group bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 border border-stone-100 overflow-hidden flex flex-col ${soldOut ? 'opacity-60' : ''}`}
                          >
                            {/* Image */}
                            <div className="relative aspect-[4/3] overflow-hidden bg-stone-100">
                              {product.imageUrl ? (
                                <img
                                  src={product.imageUrl}
                                  alt={product.name}
                                  className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ${soldOut ? 'grayscale' : ''}`}
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-orange-50 to-amber-50">
                                  <UtensilsCrossed size={40} className="text-orange-200" />
                                </div>
                              )}
                              {product.stockQuantity <= 0 && (
                                <span className="absolute top-3 right-3 bg-red-600 text-white text-sm font-bold px-3.5 py-1.5 rounded-full shadow-lg">
                                  {t('home:shop.soldOut')}
                                </span>
                              )}
                              {isLowStock && product.stockQuantity > 0 && (
                                <span className="absolute top-3 right-3 bg-amber-500 text-white text-sm font-bold px-3 py-1.5 rounded-full shadow-lg">
                                  {t('home:shop.almostSoldOut')}
                                </span>
                              )}
                              {qty > 0 && (
                                <span className="absolute top-3 left-3 bg-[#C2410C] text-white text-sm font-bold px-3 py-1.5 rounded-full shadow-lg">
                                  {t('home:shop.inCart', { count: qty })}
                                </span>
                              )}
                            </div>

                              {/* Info */}
                            <div className="p-5 flex flex-col flex-1">
                              <div className="flex justify-between items-start gap-2 mb-2">
                                <h4 className="text-xl sm:text-lg font-bold text-stone-800 leading-tight">{productName(product)}</h4>
                                <span className="text-2xl sm:text-xl font-bold text-[#C2410C] whitespace-nowrap">HK${product.price}</span>
                              </div>
                              <p className="text-sm text-stone-600 leading-relaxed mb-4 flex-1">{linkifyDescription(productDescription(product))}</p>

                              {/* Quantity Control（售罄或關店時禁用） */}
                              {soldOut ? (
                                <div className="flex items-center justify-center gap-2 pt-3 border-t border-stone-100">
                                  <RefreshCw size={18} className="text-orange-600 shrink-0" />
                                  <span className="text-base font-bold text-orange-600">{t('home:shop.restockNote')}</span>
                                </div>
                              ) : (
                              <div className="flex items-center justify-between pt-3 border-t border-stone-100">
                                <span className="text-sm text-stone-500 font-bold">{t('home:shop.quantity')}</span>
                                <div className="flex items-center gap-3">
                                  <button
                                    onClick={() => handleQtyChange(product.id, -1)}
                                    disabled={soldOut || isMerchantClosed}
                                    className="w-12 h-12 rounded-xl bg-stone-100 flex items-center justify-center hover:bg-stone-200 active:scale-95 transition text-stone-600 disabled:cursor-not-allowed disabled:opacity-50"
                                  >
                                    <Minus size={20} />
                                  </button>
                                  <input
                                    type="number"
                                    min={0}
                                    value={qty}
                                    disabled={soldOut || isMerchantClosed}
                                    onChange={e => setQtyDirect(product.id, parseInt(e.target.value) || 0)}
                                    className="w-14 h-12 text-center text-xl font-bold border border-stone-200 rounded-xl outline-none focus:border-[#C2410C] bg-white text-stone-800 disabled:bg-stone-100 disabled:text-stone-400 disabled:cursor-not-allowed"
                                  />
                                  <button
                                    onClick={() => handleQtyChange(product.id, 1)}
                                    disabled={soldOut || isMerchantClosed}
                                    className="w-12 h-12 rounded-xl bg-[#FFF7ED] flex items-center justify-center hover:bg-orange-100 active:scale-95 transition text-[#C2410C] disabled:cursor-not-allowed disabled:opacity-50"
                                  >
                                    <Plus size={20} />
                                  </button>
                                </div>
                              </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {totalProductPages > 1 && (
                      <div className="flex items-center justify-center gap-2 mt-8">
                        <button
                          onClick={() => setProductPage(p => Math.max(1, p - 1))}
                          disabled={productPage <= 1}
                          className="flex items-center gap-1 px-4 py-3 rounded-xl border border-stone-200 bg-white text-stone-600 text-base font-bold hover:bg-stone-50 disabled:opacity-40 disabled:cursor-not-allowed transition min-h-[48px]"
                        >
                          <ChevronLeft size={20} />
                          {t('home:shop.prevPage')}
                        </button>
                        <span className="text-base text-stone-600 font-bold px-4">
                          {t('home:shop.pageIndicator', { page: productPage, total: totalProductPages })}
                        </span>
                        <button
                          onClick={() => setProductPage(p => Math.min(totalProductPages, p + 1))}
                          disabled={productPage >= totalProductPages}
                          className="flex items-center gap-1 px-4 py-3 rounded-xl border border-stone-200 bg-white text-stone-600 text-base font-bold hover:bg-stone-50 disabled:opacity-40 disabled:cursor-not-allowed transition min-h-[48px]"
                        >
                          {t('home:shop.nextPage')}
                          <ChevronRight size={20} />
                        </button>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </section>

          <EstateRequestSection gsCode={gsCode} />
          {renderWhatsAppCta()}
          <SiteFooter section={footerSection} />
        </>
      )}

      {/* ===== Step: Cart Review ===== */}
      {step === 'cart' && (
        <section className="py-8 px-4">
          <div className="max-w-2xl mx-auto">
            {renderCheckoutProgress('cart')}
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-[#C2410C] rounded-full flex items-center justify-center text-white text-lg font-bold shadow-lg">2</div>
              <h3 className="text-2xl font-bold text-stone-800">{t('order:cart.title')}</h3>
            </div>

            {cart.length === 0 ? (
              <div className="bg-white rounded-2xl p-8 text-center shadow-sm border border-stone-100">
                <ShoppingCart size={48} className="mx-auto text-stone-300 mb-4" />
                <p className="text-lg text-stone-500 mb-4">{t('order:cart.empty')}</p>
                <button
                  onClick={() => setStep('menu')}
                  className="bg-[#C2410C] text-white text-lg font-bold py-3 px-8 rounded-xl hover:bg-[#EA580C] shadow-lg transition"
                >
                  {t('order:cart.backToShop')}
                </button>
              </div>
            ) : (
              <>
                <div className="space-y-3 mb-6">
                  {cart.map(item => (
                    <div key={item.productId} className="bg-white rounded-xl p-4 shadow-sm border border-stone-100 flex flex-col gap-2">
                      <div className="flex items-start justify-between gap-3">
                        <div className="text-lg sm:text-base font-bold text-stone-800 leading-tight">{productName(findProduct(item.productId) || { name: item.name })}</div>
                        <div className="text-base sm:text-sm font-bold text-stone-500 whitespace-nowrap">{t('order:cart.perUnit', { price: item.unitPrice })}</div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleCartQtyChange(item.productId, -1)}
                            className="w-10 h-10 sm:w-9 sm:h-9 bg-stone-100 rounded-lg flex items-center justify-center hover:bg-stone-200 min-h-[40px] sm:min-h-[36px]"
                            aria-label={t('order:cart.decreaseQty')}
                          >
                            <Minus size={18} className="sm:w-4 sm:h-4" />
                          </button>
                          <span className="text-lg sm:text-base font-bold w-8 text-center">{item.quantity}</span>
                          <button
                            onClick={() => handleCartQtyChange(item.productId, 1)}
                            className="w-10 h-10 sm:w-9 sm:h-9 bg-[#FFF7ED] rounded-lg flex items-center justify-center hover:bg-orange-100 min-h-[40px] sm:min-h-[36px]"
                            aria-label={t('order:cart.increaseQty')}
                          >
                            <Plus size={18} className="text-[#C2410C] sm:w-4 sm:h-4" />
                          </button>
                        </div>
                        <div className="text-lg sm:text-base font-bold text-[#C2410C]">HK${item.subtotal}</div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="bg-gradient-to-r from-[#FFF7ED] to-amber-50 rounded-xl p-6 mb-6 border border-orange-100">
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-stone-600">{t('order:cart.subtotal')}</span>
                      <span className="font-bold text-stone-800">HK${cartTotal}</span>
                    </div>
                    {previewDiscount > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-green-700 font-medium">{t('order:cart.voucherDiscount')}</span>
                        <span className="font-bold text-green-600">−HK${previewDiscount}</span>
                      </div>
                    )}
                    {deliveryFee > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-stone-600">{t('order:cart.deliveryFee')}</span>
                        <span className="font-bold text-[#C2410C]">HK${deliveryFee}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center pt-2 border-t border-orange-200">
                      <span className="text-xl font-bold text-stone-800">{t('order:cart.payableTotal')}</span>
                      <span className="text-3xl font-bold text-[#C2410C]">HK${payableTotal}</span>
                    </div>
                  </div>
                  {/* 回贈券賺取提示：以商品實付（扣減後、不含運費）為準，與後端口徑一致 */}
                  {featureFlags.repurchase_voucher_enabled && (() => {
                    const goodsPaid = cartTotal - previewDiscount;
                    return goodsPaid >= orderConfig.voucher.earnThreshold ? (
                      <div className="mt-3 flex items-center gap-2 text-green-700 dark:text-green-400 text-lg sm:text-base font-bold bg-green-50 dark:bg-green-900/30 px-4 py-3 rounded-xl">
                        <span>✅</span>
                        <span>{t('order:cart.earnVoucher', { amount: orderConfig.voucher.amount })}</span>
                      </div>
                    ) : cartTotal > 0 ? (
                      <div className="mt-3 flex items-center gap-2 text-orange-700 dark:text-orange-400 text-lg sm:text-base font-bold bg-orange-50 dark:bg-orange-900/30 px-4 py-3 rounded-xl">
                        <span>🎁</span>
                        <span>{t('order:cart.addMoreToEarn', { gap: Math.max(0, Math.ceil(orderConfig.voucher.earnThreshold - goodsPaid)), amount: orderConfig.voucher.amount })}</span>
                      </div>
                    ) : null;
                  })()}
                  {renderVoucherHint()}

                  {minOrderAmount > 0 && (
                    <div className={`mt-3 flex items-center gap-2 text-lg sm:text-base font-bold px-4 py-3 rounded-xl ${productShortfall > 0 ? 'text-orange-700 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/30' : 'text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/30'}`}>
                      <span>{productShortfall > 0 ? '🔔' : '✅'}</span>
                      <span>
                        {productShortfall > 0
                          ? t('home:bottomBar.shortfall', { min: minOrderAmount, gap: productShortfall })
                          : t('home:bottomBar.reachedMin', { min: minOrderAmount })}
                      </span>
                    </div>
                  )}

                  {isMerchantClosed && (
                    <div className="mt-3 flex items-center gap-2 text-lg sm:text-base font-bold px-4 py-3 rounded-xl text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30">
                      <span>🚫</span>
                      <span>{closedMessageText}</span>
                    </div>
                  )}
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={handleBackToMenu}
                    className="flex-1 bg-white border-2 border-stone-200 text-stone-700 text-lg font-bold py-5 px-6 rounded-xl hover:bg-stone-50 transition min-h-[60px]"
                  >
                    {t('order:cart.continueShopping')}
                  </button>
                  <button
                    onClick={() => setStep('delivery')}
                    disabled={!canProceed || isMerchantClosed}
                    className={`flex-[2] text-lg font-bold py-5 px-6 rounded-xl shadow-lg flex items-center justify-center gap-2 transition min-h-[60px] ${
                      canProceed && !isMerchantClosed
                        ? 'bg-[#C2410C] text-white hover:bg-[#EA580C]'
                        : 'bg-stone-300 text-stone-500 cursor-not-allowed'
                    }`}
                  >
                    {isMerchantClosed ? t('common:status.storeClosed') : t('order:cart.confirmFillInfo')}
                    {!isMerchantClosed && <ChevronRight size={24} />}
                  </button>
                </div>
              </>
            )}
          </div>
        </section>
      )}

      {/* ===== Step: Delivery Form ===== */}
      {step === 'delivery' && cart.length > 0 && (
        <section className="py-8 px-4">
          <div className="max-w-2xl mx-auto">
            {renderCheckoutProgress('delivery')}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#C2410C] rounded-full flex items-center justify-center text-white text-lg font-bold shadow-lg">3</div>
                <h3 className="text-2xl font-bold text-stone-800">{fulfillment === 'pickup' ? t('order:form.pickupInfo') : t('order:form.deliveryInfo')}</h3>
              </div>
              <button
                type="button"
                onClick={handleBackFromDelivery}
                className="text-base font-bold text-[#C2410C] underline"
              >
                {t('order:form.back')}
              </button>
            </div>

            <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm border border-stone-100 p-6 md:p-8 space-y-5">
              {/* Step 1: 選擇屋苑（searchable autocomplete） */}
              {!delivery.estate && (
                <div className="space-y-4">
                  <label className="block text-lg font-bold text-stone-800">{t('order:form.selectEstate')}</label>
                  {estates.length === 0 ? (
                    <p className="text-base text-red-600 font-bold">{t('order:form.noEstates')}</p>
                  ) : (
                    <div className="relative">
                      <div className="relative">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" size={22} />
                        <input
                          type="text"
                          value={estateSearchInput}
                          onChange={e => setEstateSearchInput(e.target.value)}
                          placeholder={t('order:form.estateSearchPlaceholder')}
                          className="w-full pl-12 pr-4 py-4 text-lg border-2 border-stone-200 rounded-xl focus:border-[#C2410C] focus:ring-2 focus:ring-[#C2410C]/20 outline-none transition bg-white dark:bg-gray-800 dark:text-white min-h-[60px]"
                          autoComplete="off"
                        />
                      </div>
                      {estateSearchInput.trim() && (
                        <div className="absolute z-10 left-0 right-0 mt-1 max-h-72 overflow-y-auto bg-white dark:bg-gray-800 border border-stone-200 dark:border-gray-600 rounded-xl shadow-lg">
                          {filteredEstates.length === 0 ? (
                            <div className="p-4 text-stone-500 text-base">
                              {t('order:form.estateNotFound')}
                            </div>
                          ) : (
                            filteredEstates.map(e => {
                              const canDelivery = (e.supportsDelivery ?? 1) === 1;
                              const canPickup = (e.supportsPickup ?? 0) === 1;
                              return (
                                <button
                                  key={e.id}
                                  type="button"
                                  onClick={() => {
                                    setDelivery(prev => ({ ...prev, estate: e.name, region: e.region }));
                                    if (!merchantInfo) {
                                      setFulfillment('delivery');
                                    } else {
                                      setFulfillment(canDelivery && !canPickup ? 'delivery' : canPickup && !canDelivery ? 'pickup' : null);
                                    }
                                    resetLeadState();
                                    saveEstatePreference(e.name);
                                    setEstateSearchInput('');
                                  }}
                                  className="w-full text-left px-4 py-3 hover:bg-orange-50 dark:hover:bg-gray-700 border-b border-stone-100 dark:border-gray-700 last:border-0 flex items-baseline justify-between gap-2"
                                >
                                  <span className="font-bold text-stone-800 dark:text-white">{e.name}</span>
                                  <span className="text-sm text-stone-500 whitespace-nowrap">{[e.district, e.area].filter(Boolean).join(' · ') || e.region}</span>
                                </button>
                              );
                            })
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Step 2: 已選屋苑，選擇取貨方式 */}
              {delivery.estate && delivery.estate !== OTHER_ESTATE && !fulfillment && selectedEstate && (
                <div className="space-y-4">
                  <div className="bg-stone-50 rounded-xl p-4">
                    <p className="text-sm text-stone-500 font-medium">{t('order:form.selectedEstate')}</p>
                    <div className="flex items-baseline gap-2 mt-1">
                      <p className="text-xl font-bold text-stone-800">{delivery.estate}</p>
                      <p className="text-sm text-stone-500">{selectedEstate.area || selectedEstate.region}</p>
                    </div>

                  </div>
                  <p className="text-base text-stone-600">{t('order:form.chooseFulfillment')}</p>
                  <div className={`grid gap-3 ${merchantInfo && (selectedEstate.supportsPickup ?? 0) === 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                    {((selectedEstate.supportsDelivery ?? 1) === 1) && (
                      <button
                        type="button"
                        onClick={() => setFulfillment('delivery')}
                        className={`px-3 py-5 sm:p-6 rounded-xl border-2 border-stone-200 bg-white hover:border-[#C2410C] hover:bg-orange-50 transition flex flex-col items-center gap-2 sm:gap-3 min-h-[140px] sm:min-h-[160px] ${merchantInfo && (selectedEstate.supportsPickup ?? 0) === 1 ? '' : 'max-w-md mx-auto w-full'}`}
                      >
                        <span className="text-5xl sm:text-4xl">🚚</span>
                        <span className="font-bold text-2xl sm:text-2xl">{t('order:form.delivery')}</span>
                      </button>
                    )}
                    {merchantInfo && ((selectedEstate.supportsPickup ?? 0) === 1) && (
                      <button
                        type="button"
                        onClick={() => setFulfillment('pickup')}
                        className="px-3 py-4 sm:p-5 rounded-xl border-2 border-stone-200 bg-white hover:border-[#C2410C] hover:bg-orange-50 transition flex flex-col items-center gap-1 sm:gap-2 min-h-[140px] sm:min-h-[160px]"
                      >
                        <span className="text-4xl sm:text-4xl">🏪</span>
                        <span className="font-bold text-2xl sm:text-2xl">{t('order:form.pickup')}</span>
                        <span className="text-xs sm:text-sm text-stone-600 text-center line-clamp-2 leading-tight">
                          {selectedEstate.pickupLocation || orderConfig.pickup.location}
                        </span>
                        <span className="text-xs sm:text-sm text-[#C2410C] text-center font-bold leading-tight">
                          {selectedEstate.pickupTimeNote || orderConfig.pickup.timeNote}
                        </span>
                      </button>
                    )}
                    {((selectedEstate.supportsDelivery ?? 1) !== 1) && (!merchantInfo || (selectedEstate.supportsPickup ?? 0) !== 1) && (
                      <p className="col-span-full text-base text-red-600 font-bold">{t('order:form.noServiceMode')}</p>
                    )}
                  </div>
                </div>
              )}

              {/* ===== 「其他屋苑」雙軌：轉介鄰里店 / 直接落單 ===== */}
              {delivery.estate === OTHER_ESTATE && !fulfillment && leadStatus !== 'success' && otherEstateMode !== 'lead' && (
                <div
                  id="other-estate-box"
                  className="scroll-mt-28 bg-amber-50 dark:bg-amber-900/20 border-2 border-[#C2410C] dark:border-amber-500 rounded-2xl p-5 sm:p-6 space-y-4 shadow-2xl ring-4 ring-[#C2410C]/20 animate-attention-pulse"
                >
                  <div className="flex items-start gap-3">
                    <span className="text-2xl shrink-0">👇</span>
                    <div>
                      <p className="font-bold text-amber-800 dark:text-amber-200 text-lg sm:text-xl">{t('order:otherEstate.inputTitle')}</p>
                      <p className="text-base text-amber-700 dark:text-amber-300 mt-1 leading-relaxed">
                        {t('order:otherEstate.checkHint')}
                      </p>
                    </div>
                  </div>
                  <input
                    type="text"
                    value={otherEstateName}
                    onChange={e => {
                      setOtherEstateName(e.target.value);
                      setLeadEstateName(e.target.value);
                    }}
                    placeholder={t('home:estateRequest.estateNamePlaceholder')}
                    autoFocus
                    className="w-full p-4 text-lg border-2 border-[#C2410C] dark:border-amber-500 rounded-xl focus:ring-4 focus:ring-[#C2410C]/30 focus:border-[#C2410C] outline-none transition bg-white dark:bg-gray-800 dark:text-white min-h-[56px] shadow-inner"
                  />

                  {otherEstateName.trim().length >= 2 && otherEstateMatches.length > 0 && (
                    <div className="space-y-3">
                      <p className="text-sm font-bold text-stone-700 dark:text-stone-200">
                        {t('home:finder.choosePrefix')}<span className="text-[#C2410C]">{t('home:finder.storeWord')}</span>{t('home:finder.chooseSuffix')}
                      </p>
                      {otherEstateMatches.map(m => (
                        <button
                          key={m.code}
                          type="button"
                          onClick={() => handleSwitchMerchant(m.code, otherEstateName.trim())}
                          className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-600 rounded-xl text-stone-800 dark:text-stone-100 hover:border-[#C2410C] hover:bg-orange-50 dark:hover:bg-stone-700 transition text-left"
                        >
                          <div>
                            <p className="font-bold text-base">{m.isBackup ? t('home:finder.backupStoreName', { name: m.name }) : t('home:finder.storeName', { name: m.name })}</p>
                            <p className="text-xs text-stone-500 dark:text-stone-400 mt-0.5">{formatDeliveryDays(m.deliveryDays)}</p>
                          </div>
                          <div className="text-right shrink-0">
                            <p className="text-xs text-stone-500 dark:text-stone-400">{t('home:finder.freeShippingMinLabel')}</p>
                            <p className="font-bold text-[#C2410C]">HK${estateMinForMerchant(m, otherEstateName.trim())}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {otherEstateName.trim().length >= 2 && otherEstateMatches.length === 0 && (
                    <div className="space-y-3">
                      <p className="text-base text-amber-800 dark:text-amber-200 font-bold">{t('order:otherEstate.directIntro')}</p>
                      <button
                        type="button"
                        onClick={() => setFulfillment('delivery')}
                        className="w-full bg-orange-600 hover:bg-orange-700 text-white text-lg font-bold py-4 rounded-xl shadow-lg transition min-h-[56px]"
                      >
                        {t('order:otherEstate.continueOrder')}
                      </button>
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={() => { setDelivery(prev => ({ ...prev, estate: '' })); setOtherEstateName(''); setOtherEstateMode(null); resetLeadState(); }}
                    className="w-full bg-white dark:bg-gray-800 border-2 border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300 text-lg font-bold py-4 rounded-xl hover:bg-amber-50 dark:hover:bg-amber-900/30 transition min-h-[56px]"
                  >
                    {t('order:otherEstate.backToEstate')}
                  </button>
                </div>
              )}

              {/* ===== 「其他屋苑」需求登記卡 ===== */}
              {delivery.estate === OTHER_ESTATE && leadStatus !== 'success' && otherEstateMode === 'lead' && (
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-xl p-5 space-y-4">
                  <div>
                    <p className="font-bold text-amber-800 dark:text-amber-200 text-lg">{t('order:otherEstate.leadTitle')}</p>
                    <p className="text-base text-amber-700 dark:text-amber-300 mt-1 leading-relaxed">
                      {t('order:otherEstate.leadHint')}
                    </p>
                  </div>
                  <div>
                    <label className="block text-base font-bold text-amber-800 dark:text-amber-200 mb-2">{t('home:estateRequest.typeLabel')}</label>
                    <div className="space-y-2">
                      {REQUESTER_TYPE_OPTIONS.map(opt => (
                        <label
                          key={opt.value}
                          className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition ${
                            leadType === opt.value
                              ? 'border-amber-600 bg-amber-100 dark:bg-amber-900/40'
                              : 'border-amber-300 dark:border-amber-600 bg-white dark:bg-gray-800 hover:border-amber-400'
                          }`}
                        >
                          <input
                            type="radio"
                            name="leadType"
                            value={opt.value}
                            checked={leadType === opt.value}
                            onChange={e => setLeadType(e.target.value as RequesterType)}
                            className="w-5 h-5 text-amber-600 focus:ring-amber-500"
                          />
                          <div>
                            <p className="font-bold text-amber-900 dark:text-amber-100">{t(opt.labelKey)}</p>
                            <p className="text-sm text-amber-700 dark:text-amber-300">{t(opt.descKey)}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-base font-bold text-amber-800 dark:text-amber-200 mb-2">{t('home:estateRequest.estateNameLabel')}</label>
                    <input
                      type="text"
                      value={leadEstateName}
                      onChange={e => setLeadEstateName(e.target.value)}
                      placeholder={t('home:estateRequest.estateNamePlaceholder')}
                      className="w-full p-4 text-lg border border-amber-300 dark:border-amber-600 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none transition bg-white dark:bg-gray-800 dark:text-white min-h-[56px]"
                    />
                  </div>
                  <div>
                    <label className="block text-base font-bold text-amber-800 dark:text-amber-200 mb-2">{t('home:estateRequest.districtLabel')}</label>
                    <select
                      value={leadDistrict}
                      onChange={e => setLeadDistrict(e.target.value)}
                      className="w-full p-4 text-lg border border-amber-300 dark:border-amber-600 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none transition bg-white dark:bg-gray-800 dark:text-white min-h-[56px]"
                    >
                      <option value="">{t('home:estateRequest.districtPlaceholder')}</option>
                      {HK_DISTRICT_GROUPS.map(g => (
                        <optgroup key={g.region} label={g.region}>
                          {g.areas.map(a => (
                            <option key={a} value={a}>{a}</option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-base font-bold text-amber-800 dark:text-amber-200 mb-2">{t('home:estateRequest.phoneLabel')}</label>
                    <input
                      type="tel"
                      inputMode="tel"
                      value={leadPhone}
                      onChange={e => setLeadPhone(e.target.value)}
                      placeholder={t('home:estateRequest.phonePlaceholder')}
                      className="w-full p-4 text-lg border border-amber-300 dark:border-amber-600 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none transition bg-white dark:bg-gray-800 dark:text-white min-h-[56px]"
                    />
                  </div>
                  {/* Honeypot 防機器人欄位（不顯示） */}
                  <input
                    type="text"
                    name="website"
                    value={leadWebsite}
                    onChange={e => setLeadWebsite(e.target.value)}
                    style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                  />
                  {leadError && (
                    <p className="text-base text-red-600 dark:text-red-400 font-bold bg-red-50 dark:bg-red-900/30 px-4 py-3 rounded-xl">{leadError}</p>
                  )}
                  <button
                    type="button"
                    onClick={handleLeadSubmit}
                    disabled={leadStatus === 'submitting'}
                    className="w-full bg-orange-600 hover:bg-orange-700 text-white text-lg font-bold py-5 px-6 rounded-xl shadow-lg transition flex items-center justify-center gap-2 disabled:opacity-60 min-h-[60px]"
                  >
                    {leadStatus === 'submitting' ? (
                      <><Loader2 className="animate-spin" size={24} /> {t('order:form.submitting')}</>
                    ) : (
                      <>{t('order:otherEstate.notifyButton')}</>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => setOtherEstateMode(null)}
                    className="w-full bg-white dark:bg-gray-800 border-2 border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300 text-lg font-bold py-4 rounded-xl hover:bg-amber-50 dark:hover:bg-amber-900/30 transition min-h-[56px]"
                  >
                    {t('order:otherEstate.backToSearch')}
                  </button>
                </div>
              )}

              {/* ===== 「其他屋苑」登記成功卡（全畫面取代表單） ===== */}
              {delivery.estate === OTHER_ESTATE && leadStatus === 'success' && leadResult && (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-300 dark:border-green-700 rounded-xl p-5 space-y-4">
                  {leadResult.duplicate ? (
                    <p className="font-bold text-green-800 dark:text-green-200 text-lg">{t('order:otherEstate.dupRegistered')}</p>
                  ) : (
                    <div>
                      <p className="font-bold text-green-800 dark:text-green-200 text-lg">{t('order:otherEstate.recorded')}</p>
                      <p className="text-base text-green-700 dark:text-green-300 mt-2 leading-relaxed">
                        {t('order:otherEstate.votes', { estate: leadEstateName, count: leadResult.estateCount })}
                      </p>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={handleLeadShare}
                    className="w-full bg-[#25D366] hover:bg-[#128C7E] text-white text-lg font-bold py-5 px-6 rounded-xl shadow-lg transition flex items-center justify-center gap-2 min-h-[60px]"
                  >
                    {t('order:otherEstate.shareButton')}
                  </button>
                  <p className="text-sm text-green-600 dark:text-green-400 text-center">
                    {t('order:otherEstate.referralHint')}
                  </p>
                  <button
                    type="button"
                    onClick={handleLeadBack}
                    className="w-full bg-white dark:bg-gray-800 border-2 border-green-300 dark:border-green-700 text-green-700 dark:text-green-300 text-lg font-bold py-4 rounded-xl hover:bg-green-50 dark:hover:bg-green-900/30 transition min-h-[56px]"
                  >
                    {t('order:otherEstate.reselectEstate')}
                  </button>
                </div>
              )}

              {/* Step 3: 已選屋苑及取貨方式，顯示地址 / 日期 / 個人資料 */}
              {fulfillment && delivery.estate && (
                <>
                  <div className="flex items-start justify-between bg-stone-50 rounded-xl p-4 gap-3">
                    <div>
                      <p className="text-sm text-stone-500 font-medium">{t('order:form.estateLabel')}</p>
                      <div className="flex items-baseline gap-2">
                        <p className="font-bold text-lg text-stone-800">{delivery.estate === OTHER_ESTATE ? otherEstateName.trim() : delivery.estate}</p>
                        <p className="text-sm text-stone-500">{delivery.estate === OTHER_ESTATE ? '' : (selectedEstate?.area || selectedEstate?.region)}</p>
                      </div>

                      {delivery.estate === OTHER_ESTATE && masterDeliveryMin > 0 && (
                        <p className="text-sm text-stone-600 mt-1">{t('order:form.directMinLabel', { amount: masterDeliveryMin })}</p>
                      )}

                    </div>

                  </div>

                  {fulfillment === 'delivery' && (
                    <div>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <input
                          type="text"
                          name="addressBuilding"
                          placeholder={t('order:form.buildingPlaceholder')}
                          value={delivery.addressBuilding}
                          onChange={handleDeliveryChange}
                          className="w-full p-4 text-lg border border-stone-200 rounded-xl focus:ring-2 focus:ring-[#C2410C] focus:border-[#C2410C] outline-none transition min-h-[56px]"
                        />
                        <input
                          type="text"
                          name="addressFloor"
                          placeholder={t('order:form.floorPlaceholder')}
                          required
                          value={delivery.addressFloor}
                          onChange={handleDeliveryChange}
                          className="w-full p-4 text-lg border border-stone-200 rounded-xl focus:ring-2 focus:ring-[#C2410C] focus:border-[#C2410C] outline-none transition min-h-[56px]"
                        />
                        <input
                          type="text"
                          name="addressUnit"
                          placeholder={t('order:form.unitPlaceholder')}
                          required
                          value={delivery.addressUnit}
                          onChange={handleDeliveryChange}
                          className="w-full p-4 text-lg border border-stone-200 rounded-xl focus:ring-2 focus:ring-[#C2410C] focus:border-[#C2410C] outline-none transition min-h-[56px]"
                        />
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-base font-bold text-stone-700 mb-2">
                      {fulfillment === 'pickup' ? t('order:form.pickupDate') : t('order:form.deliveryDate')}
                      {delivery.deliveryDate && <span className="ml-1 text-[#C2410C]">（{formatWeekday(delivery.deliveryDate)}）</span>}
                    </label>
                    <input
                      type="date"
                      name="deliveryDate"
                      required
                      min={minDateStr}
                      value={delivery.deliveryDate}
                      onChange={handleDeliveryChange}
                      className="w-full p-4 text-lg border border-stone-200 rounded-xl focus:ring-2 focus:ring-[#C2410C] focus:border-[#C2410C] outline-none transition min-h-[56px]"
                    />
                    {merchantInfo ? (
                      <p className="text-base text-[#C2410C] mt-2 font-bold">
                        ⏰ {merchantInfo.deliveryDays?.length ? t('order:form.merchantDeliveryDays', { days: formatDeliveryDays(merchantInfo.deliveryDays) }) : t('order:form.merchantDailyDelivery')}
                        {merchantInfo.deliveryTime ? ` · ${merchantInfo.deliveryTime}` : ''}
                        {merchantInfo.deliveryCutoffTime ? ` · ${t('order:form.cutoffSuffix', { time: merchantInfo.deliveryCutoffTime })}` : ''}
                      </p>
                    ) : (
                      <p className="text-base text-[#C2410C] mt-2 font-bold">{t('order:form.dailyCutoff', { time: orderConfig.cutoffTime })}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-base font-bold text-stone-700 mb-2">{t('order:form.contactName')}</label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={delivery.name}
                      onChange={handleDeliveryChange}
                      className="w-full p-4 text-lg border border-stone-200 rounded-xl focus:ring-2 focus:ring-[#C2410C] focus:border-[#C2410C] outline-none transition min-h-[56px]"
                    />
                  </div>

                  <div>
                    <label className="block text-base font-bold text-stone-700 mb-2">{t('order:form.whatsappPhone')}</label>
                    <input
                      type="tel"
                      name="contactPhone"
                      inputMode="tel"
                      placeholder={t('home:estateRequest.phonePlaceholder')}
                      required
                      value={delivery.contactPhone}
                      onChange={handleDeliveryChange}
                      className={`w-full p-4 text-lg border rounded-xl focus:ring-2 focus:ring-[#C2410C] focus:border-[#C2410C] outline-none transition min-h-[56px] ${
                        deliveryPhoneError ? 'border-red-300 bg-red-50' : 'border-stone-200'
                      }`}
                    />
                    {deliveryPhoneError && (
                      <p className="text-base text-red-600 mt-2 font-bold">{deliveryPhoneError}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-base font-bold text-stone-700 mb-2">{t('order:form.remarksLabel')}</label>
                    <textarea
                      name="remarks"
                      rows={3}
                      placeholder={t('order:form.remarksPlaceholder')}
                      value={delivery.remarks}
                      onChange={handleDeliveryChange}
                      className="w-full p-4 text-lg border border-stone-200 rounded-xl focus:ring-2 focus:ring-[#C2410C] focus:border-[#C2410C] outline-none transition resize-none min-h-[120px]"
                    />
                  </div>

                  {isMerchantClosed && (
                    <p className="text-lg text-red-600 bg-red-50 p-4 rounded-xl text-center font-bold">
                      🚫 {closedMessageText}
                    </p>
                  )}

                  {submitError && (
                    <p className="text-lg text-red-600 bg-red-50 p-4 rounded-xl text-center font-bold">{submitError}</p>
                  )}

                  {renderVoucherHint()}

                  <button
                    type="submit"
                    disabled={isSubmitting || isMerchantClosed}
                    onClick={() => trackWhatsAppClick(undefined, 'order_submit')}
                    className={`w-full text-white text-lg font-bold py-5 px-6 rounded-xl shadow-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-60 min-h-[64px] ${
                      isMerchantClosed
                        ? 'bg-stone-500 cursor-not-allowed'
                        : 'bg-[#25D366] hover:bg-[#128C7E]'
                    }`}
                  >
                    {isSubmitting ? (
                      <><Loader2 className="inline animate-spin mr-2" size={24} /> {t('order:form.submitting')}</>
                    ) : isMerchantClosed ? (
                      <span className="flex flex-col items-center leading-tight">
                        <span>{t('common:status.storeClosed')}</span>
                      </span>
                    ) : (
                      <>
                        <MessageCircle size={24} />
                        <span className="flex flex-col items-center leading-tight">
                          <span>{t('order:form.whatsappOrder')}</span>
                          <span>{t('order:form.confirmPay', { amount: payableTotal })}</span>
                        </span>
                      </>
                    )}
                  </button>
                </>
              )}
            </form>
          </div>
        </section>
      )}

      {/* ===== Fixed Bottom Bar (Menu Step) ===== */}
      {step === 'menu' && (
        <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md border-t border-stone-200 shadow-[0_-4px_24px_rgba(0,0,0,0.08)] z-50">
          <div className="max-w-5xl mx-auto px-4 py-3">
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <div className="text-2xl font-bold text-stone-800">HK${menuTotal}</div>
                <div className="text-base text-stone-500 font-bold">{t('home:bottomBar.items', { count: menuItemCount })}</div>
              </div>
              <button
                onClick={handleGoToCart}
                disabled={menuItemCount === 0 || isMerchantClosed}
                className={`px-8 py-4 rounded-xl text-lg font-bold shadow flex items-center gap-2 transition min-h-[56px] ${
                  menuItemCount > 0 && !isMerchantClosed
                    ? 'bg-[#C2410C] text-white hover:bg-[#EA580C]'
                    : 'bg-stone-300 text-stone-500 cursor-not-allowed'
                }`}
              >
                {isMerchantClosed ? t('common:status.storeClosed') : t('common:actions.checkout')}
                {!isMerchantClosed && <ChevronRight size={24} />}
              </button>
            </div>
            {(isMerchantClosed || menuItemCount > 0) && (
              <div className="mt-2 text-right">
                {isMerchantClosed ? (
                  <p className="text-base text-red-600 font-bold inline-flex items-center gap-1">
                    <AlertCircle size={16} /> {closedMessageText}
                  </p>
                ) : minOrderAmount > 0 && productShortfall > 0 ? (
                  <p className="text-base text-orange-600 font-bold inline-flex items-center gap-1">
                    <AlertCircle size={16} /> {t('home:bottomBar.shortfall', { min: minOrderAmount, gap: productShortfall })}
                  </p>
                ) : (
                  <p className="text-base text-green-600 font-bold inline-flex items-center gap-1">
                    <CheckCircle size={16} /> {minOrderAmount > 0 ? t('home:bottomBar.reachedMin', { min: minOrderAmount }) : t('home:bottomBar.canCheckout')}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderLanding;
